// ========================================
// Professor Profile View
// Updated: 2026-01-31 - v1.4 (Force Update)
// ========================================

const ProfessorProfile = {
    selectedFormationId: null,
    lastProfessorId: null,
    selectedYearStart: null,
    // Ficha del profesor, diseño «D» (2026-10-23): identity column on the left; on the right
    // what is pending and the sections as tabs, «Resumen» (cards that open each tab) first.
    activeTab: 'resumen',

    render(professorId) {
        // Reset selected formation if viewing a different professor
        if (this.lastProfessorId !== professorId) {
            this.selectedFormationId = null;
            this.selectedYearStart = null;
            this.lastProfessorId = professorId;
            this.activeTab = 'resumen';
        }

        if (!this.selectedYearStart) {
            const today = new Date();
            this.selectedYearStart = today.getMonth() >= 8 ? today.getFullYear() : today.getFullYear() - 1;
        }

        // Only this professor's classes of the selected course (statistics run to 30 September).
        if (typeof FileStorage !== 'undefined' && FileStorage.needClasses) {
            const hash = App.currentRoute();
            FileStorage.needClasses({ from: `${this.selectedYearStart}-09-01`, to: `${this.selectedYearStart + 1}-09-30`, professorId }, () => {
                if (App.currentRoute() === hash) App.navigate(hash);
            });
        }

        const professor = DataManager.getProfessor(professorId);
        if (!professor) {
            return `
                <div class="view-container dnr-proposal fade-in ppf-page" id="professor-profile-container">
                    <div class="dnr-empty"><p>Profesor no encontrado.</p></div>
                </div>
            `;
        }

        const canSeeSalaries = (typeof AuthManager !== 'undefined') && AuthManager.isLoggedIn() ? AuthManager.canSeeSalaries() : true;
        const classes = DataManager.getClassesByProfessor(professorId);

        // Get unique formations for this professor and ensure one is selected
        const formationIds = [...new Set(classes.map(c => c.formationId))];
        const formations = formationIds
            .map(id => DataManager.getFormation(id))
            .filter(Boolean);

        if (!this.selectedFormationId && formations.length > 0) {
            this.selectedFormationId = formations[0].id;
        }

        // Estadísticas (horas, coste, nómina) acotadas al curso académico seleccionado
        // en el desplegable — cambiar de curso debe actualizar las cifras a la vez.
        const stats = this.getYearScopedStats(professor, this.selectedYearStart);
        // Hide costs if not allowed
        if (!canSeeSalaries) Object.assign(stats, { weeklyCost: 0, monthlyPayroll: 0 });

        const isSelf = this.isOwnProfile(professor);
        const ctx = { professor, classes, formations, stats, canSeeSalaries, isSelf,
            // The person's own ficha without staff rights: no figures of pay and no management.
            selfView: isSelf && !this.canManageStaff() && !canSeeSalaries,
            employmentStatus: this.getEmploymentStatus(professor) };
        const tabs = this.profileTabs(ctx);
        if (!tabs.some(tab => tab.id === this.activeTab)) this.activeTab = 'resumen';

        const html = `
            <div class="view-container dnr-proposal fade-in ppf-page" id="professor-profile-container">
                <div class="ppf">
                    <aside class="ppf-id" aria-label="Datos de ${Utils.sanitizeHTML(`${professor.firstName} ${professor.lastName}`)}">
                        ${this.renderIdentity(ctx)}
                    </aside>
                    <div class="ppf-main">
                        ${this.renderPending(ctx, tabs)}
                        <div class="ppf-tabs" role="tablist" aria-label="Secciones de la ficha">
                            ${tabs.map(tab => `<button type="button" role="tab" class="ppf-tab ${tab.id === this.activeTab ? 'is-on' : ''}" id="ppf-tab-${tab.id}" data-ppf-tab="${tab.id}"
                                    aria-selected="${tab.id === this.activeTab}" aria-controls="ppf-pane-${tab.id}"
                                    ${UIEvents.attr("click", function(event,captured){ProfessorProfile.switchTab(String(captured[0]));}, [tab.id])}>
                                ${tab.label}<span class="ppf-badge" id="ppf-badge-${tab.id}" ${tab.badge ? '' : 'hidden'}>${tab.badge || ''}</span>
                            </button>`).join('')}
                        </div>
                        ${tabs.map(tab => `<section class="ppf-pane" id="ppf-pane-${tab.id}" data-pane="${tab.id}" role="tabpanel" aria-labelledby="ppf-tab-${tab.id}" ${tab.id === this.activeTab ? '' : 'hidden'}>
                            ${tab.html}
                        </section>`).join('')}
                    </div>
                </div>
            </div>
        `;

        // Trigger daily notification check (silent) after the string is returned
        // Skip this if the logged-in user is a professor to prevent 403 write permission errors on professors.json
        if (!AuthManager.isProfessor() && typeof ProfessorService !== 'undefined' && ProfessorService.checkDailyAdvisoryNotification) {
            ProfessorService.checkDailyAdvisoryNotification(professorId);
        }

        return html;
    },

    /** The person looking at their own ficha (whatever their role). */
    isOwnProfile(professor) {
        const own = typeof AuthManager !== 'undefined' ? AuthManager.currentUser?.professorId : null;
        return !!own && String(own) === String(professor.id);
    },

    /** The tabs this viewer may open, in order, each with its content and its badge. */
    profileTabs(ctx) {
        const { professor } = ctx;
        const tabs = [
            { id: 'resumen', label: 'Resumen', html: '' },
            { id: 'clases', label: 'Clases', html: this.renderClassesPane(ctx) }
        ];
        const work = this.renderWorkCalendar(professor);
        if (work) tabs.push({ id: 'jornada', label: 'Jornada', html: work });
        if (this.canManageStaff() || ctx.canSeeSalaries) {
            tabs.push({ id: 'contratos', label: 'Contratos y nómina', html: this.renderContractsPane(ctx), badge: this.pendingAdvisoryContracts(professor).length || '' });
        }
        // Loaded after the ficha is drawn: its badge and its card come with the data.
        const leaves = this.renderLeaveSection(professor);
        if (leaves) tabs.push({ id: 'vacaciones', label: 'Vacaciones y permisos', html: leaves });
        if (this.canSeeJustificantes(professor)) {
            tabs.push({ id: 'justificantes', label: 'Justificantes', html: this.renderJustificantesPane(ctx), badge: this.justificantesOf(professor).filter(d => !d.reviewedAt).length || '' });
        }
        ctx.tabIds = tabs.map(tab => tab.id);
        tabs[0].html = this.renderSummary(ctx);
        return tabs;
    },

    switchTab(tab) {
        const button = document.getElementById(`ppf-tab-${tab}`);
        if (!button) return;
        this.activeTab = tab;
        document.querySelectorAll('.ppf-tab').forEach(item => {
            const on = item === button;
            item.classList.toggle('is-on', on);
            item.setAttribute('aria-selected', String(on));
        });
        document.querySelectorAll('.ppf-pane').forEach(pane => { pane.hidden = pane.dataset.pane !== tab; });
    },

    /** A tab whose data turned out to be unavailable (no access): its button, pane and card go. */
    dropTab(tab) {
        document.getElementById(`ppf-tab-${tab}`)?.remove();
        document.getElementById(`ppf-pane-${tab}`)?.remove();
        document.querySelector(`[data-ppf-card="${tab}"]`)?.remove();
        this.setPending(tab, '');
        if (this.activeTab === tab) this.switchTab('resumen');
    },

    setBadge(tab, count) {
        const badge = document.getElementById(`ppf-badge-${tab}`);
        if (!badge) return;
        badge.textContent = count ? String(count) : '';
        badge.hidden = !count;
    },

    /** One chip of the «Pendiente» strip (empty text hides it); the strip hides with no chip. */
    setPending(tab, text) {
        const chip = document.querySelector(`[data-ppf-chip="${tab}"]`);
        if (chip) {
            chip.textContent = text;
            chip.hidden = !text;
        }
        const strip = document.getElementById('ppf-pending');
        if (strip) strip.hidden = !strip.querySelector('[data-ppf-chip]:not([hidden])');
    },

    renderPending(ctx, tabs) {
        const ids = tabs.map(tab => tab.id);
        const notices = this.pendingAdvisoryContracts(ctx.professor).length;
        const unseen = ids.includes('justificantes') && JustificantesView.isReviewer() ? this.justificantesOf(ctx.professor).filter(d => !d.reviewedAt).length : 0;
        const chips = [
            ['contratos', ids.includes('contratos') && this.canManageStaff() && notices ? `${notices === 1 ? 'Aviso' : `${notices} avisos`} a la asesoría sin enviar` : ''],
            ['vacaciones', ''],
            ['justificantes', unseen ? `${unseen} ${unseen === 1 ? 'justificante' : 'justificantes'} sin abrir` : '']
        ].filter(([id]) => ids.includes(id));
        return `<div class="ppf-pending" id="ppf-pending" ${chips.some(([, text]) => text) ? '' : 'hidden'}>
            <strong>Pendiente:</strong>
            ${chips.map(([id, text]) => `<button type="button" class="ppf-chip" data-ppf-chip="${id}" ${text ? '' : 'hidden'}
                ${UIEvents.attr("click", function(event,captured){ProfessorProfile.switchTab(String(captured[0]));}, [id])}>${text}</button>`).join('')}
        </div>`;
    },

    /** Contracts with a hire still to tell the advisory (an autónomo has none). */
    pendingAdvisoryContracts(professor) {
        return StaffPay.contractsOf(professor).filter(c => c.id && !c.freelance && !c.advisoryNotified);
    },

    /**
     * Horas asignadas y estimaciones acotadas al curso académico seleccionado
     * (Oct de yearStart a Sep de yearStart+1), como el calendario de la ficha.
     * El alta/baja actual determina el estado laboral, no borra clases históricas.
     * Estas estimaciones no son un cálculo de nóminas abonadas. Se usa tanto
     * para las tarjetas de estadísticas como para la cifra "Horas / Semana" de la
     * cabecera — cambiar el curso en el selector debe mover ambas cosas a la vez.
     */
    getYearScopedStats(professor, yearStart) {
        const zero = { weeklyHours: 0, weeklyCost: 0, monthlyPayroll: 0, totalAssignedHours: 0, payrollMonths: 0 };
        if (!professor || typeof ProfessorService === 'undefined' || !Number.isInteger(yearStart)) return zero;

        const yearRangeStart = new Date(yearStart, 9, 1); // 1 Oct
        const yearRangeEnd = new Date(yearStart + 1, 8, 30, 23, 59, 59, 999); // 30 Sep

        const totalAssignedHours = ProfessorService.getProfessorTotalHoursInPeriod(professor.id, yearRangeStart, yearRangeEnd);
        if (StaffPay.isFixed(professor)) {
            // Fixed salary: the working schedule and the net salary of each contract month of the course.
            let weeklyCost = 0, payrollMonths = 0;
            for (let month = yearStart * 12 + 9; month <= yearStart * 12 + 20; month++) {
                const cost = StaffPay.fixedCostInMonth(professor, Math.floor(month / 12), month % 12);
                if (cost > 0) { weeklyCost += cost; payrollMonths++; }
            }
            return { weeklyHours: StaffPay.weeklyHours(professor.workSchedule), weeklyCost, monthlyPayroll: StaffPay.monthlySalary(professor), totalAssignedHours, payrollMonths };
        }
        const weeklyHours = ProfessorService.getProfessorAverageWeeklyHoursInPeriod(professor.id, yearRangeStart, yearRangeEnd);

        // "Coste Estimado" (etiquetada "Por curso completo") y "Nómina" se derivan
        // de las horas asignadas en el curso, no de una media semanal
        // multiplicada a ciegas — así casan con la etiqueta y con el propio curso.
        // Each contract's hours at its own rate (rates rise from one contract to the next).
        const weeklyCost = this.costInPeriod(professor, yearRangeStart, yearRangeEnd, totalAssignedHours);

        const payrollMonths = totalAssignedHours > 0 ? this.getPayrollMonthsInYear(professor, yearStart) : 0;
        const monthlyPayroll = payrollMonths > 0 ? weeklyCost / payrollMonths : 0;

        return { weeklyHours, weeklyCost, monthlyPayroll, totalAssignedHours, payrollMonths };
    },

    /** Cost of the hours taught in a period: each contract's at its rate, the rest at the current one. */
    costInPeriod(professor, start, end, totalHours) {
        const pad = n => String(n).padStart(2, '0');
        const iso = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
        const from = iso(start), to = iso(end);
        let cost = 0, covered = 0;
        for (const c of StaffPay.contractsOf(professor)) {
            const a = c.startDate > from ? c.startDate : from, b = c.endDate && c.endDate < to ? c.endDate : to;
            if (a > b || c.hourlyRate == null) continue;
            const hours = ProfessorService.getProfessorTotalHoursInPeriod(professor.id, new Date(`${a}T00:00:00`), new Date(`${b}T23:59:59`));
            cost += hours * StaffPay.rateOn(professor, a);
            covered += hours;
        }
        return cost + Math.max(0, totalHours - covered) * StaffPay.hourlyRate(professor);
    },

    getPayrollMonthsInYear(professor, yearStart) {
        const courseStart = yearStart * 12 + 9; // October, zero-based month.
        const courseEnd = courseStart + 11;
        const teachingMonths = [];
        for (let month = courseStart; month <= courseEnd; month++) {
            const year = Math.floor(month / 12), index = month % 12;
            const start = new Date(year, index, 1);
            const end = new Date(year, index + 1, 0, 23, 59, 59, 999);
            if (ProfessorService.getProfessorTotalHoursInPeriod(professor.id, start, end) > 0) teachingMonths.push(month);
        }
        if (!teachingMonths.length) return 0;
        const first = teachingMonths[0], last = teachingMonths[teachingMonths.length - 1];
        const monthOf = value => {
            if (!value) return null;
            const date = new Date(String(value).split('T')[0] + 'T12:00:00');
            return Number.isFinite(date.getTime()) ? date.getFullYear() * 12 + date.getMonth() : null;
        };
        // Several contracts: the course months that have one.
        const contracts = StaffPay.contractsOf(professor);
        if (contracts.length > 1) {
            let months = 0;
            for (let month = courseStart; month <= courseEnd; month++) if (StaffPay.employedInMonth(professor, Math.floor(month / 12), month % 12)) months++;
            if (months) return months;
        }
        const hire = monthOf(professor.hireDate), termination = monthOf(professor.terminationDate);
        // Use contract bounds only when they cover this course's assignments.
        // A later renewal must not dictate the denominator for a historical course.
        if ((hire === null || hire <= first) && (termination === null || termination >= last)) {
            return Math.min(courseEnd, termination ?? last) - Math.max(courseStart, hire ?? first) + 1;
        }
        // No historical contract is stored: estimate its span from assigned teaching.
        // Include intermediate months even if they have no lessons (Feb-May = 4).
        return last - first + 1;
    },

    getEmploymentStatus(professor) {
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Normalize to midnight

        const hireDate = professor.hireDate ? new Date(professor.hireDate) : null;
        const termDate = professor.terminationDate ? new Date(professor.terminationDate) : null;

        // If no hire date, can't determine status
        if (!hireDate) {
            return 'inactive';
        }

        hireDate.setHours(0, 0, 0, 0); // Normalize to midnight

        // Check if professor has started yet
        if (today < hireDate) {
            // Not yet started - check if hire date is within 15 days
            const daysUntilHire = Math.ceil((hireDate - today) / (1000 * 60 * 60 * 24));
            return daysUntilHire <= 15 ? 'warning' : 'inactive';
        }

        // If no termination date, active indefinitely
        if (!termDate) {
            return 'active';
        }

        termDate.setHours(0, 0, 0, 0); // Normalize to midnight

        const daysUntilTermination = Math.ceil((termDate - today) / (1000 * 60 * 60 * 24));

        if (daysUntilTermination < 0) {
            return 'inactive'; // Already terminated
        } else if (daysUntilTermination <= 15) {
            return 'warning'; // Less than 15 days until termination
        } else {
            return 'active'; // Active with more than 15 days
        }
    },

    // The real login of this person's account, never one guessed from the name.
    // Only admins load every account; anyone else can still see their own.
    accessUsername(professor) {
        if (typeof AuthManager === 'undefined') return '';
        const own = AuthManager.currentUser;
        if (own && String(own.professorId) === String(professor.id)) return own.username || '';
        return (AuthManager.users || []).find(u => String(u.professorId) === String(professor.id))?.username || '';
    },

    renderIdentity(ctx) {
        const { professor, stats, canSeeSalaries, employmentStatus, selfView } = ctx;
        const initials = `${professor.firstName[0]}${professor.lastName[0]}`.toUpperCase();
        const username = this.accessUsername(professor);
        const fixed = StaffPay.isFixed(professor);
        const statusLabel = employmentStatus === 'active' ? 'Activo' :
            employmentStatus === 'warning' ? 'Próximo cambio' : 'Inactivo';
        const hours = n => Number(n || 0).toLocaleString('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
        const figures = [
            { value: hours(stats.weeklyHours), label: fixed ? 'Horas de jornada' : 'Horas / semana' },
            { value: Math.round(stats.totalAssignedHours).toLocaleString('es-ES'), label: 'Horas del curso' }
        ];
        if (canSeeSalaries) {
            // Hourly pay is agreed gross: the course's hours × rate over the months worked.
            figures.push({ value: Utils.formatCurrency(stats.monthlyPayroll), label: fixed ? 'Sueldo neto / mes' : 'Nómina bruta / mes', sub: fixed ? '12 pagas' : 'Estimada' });
            figures.push({ value: Utils.formatCurrency(stats.weeklyCost), label: fixed ? 'Coste del curso' : 'Bruto del curso', sub: fixed ? 'Sueldo neto del curso' : 'Estimado' });
        }
        const button = (text, fn, args, cls = '') => `<button type="button" class="ppf-id-btn ${cls}" ${UIEvents.attr("click", fn, args)}>${text}</button>`;
        const edit = AuthManager.canEditProfessorDirectory()
            ? button('<i class="fas fa-pencil-alt" aria-hidden="true"></i> Editar ficha', function(event,captured){ProfessorsView.editProfessor(String(captured[0]));}, [professor.id], 'is-primary')
            : AuthManager.currentUser?.role === 'professor' && ctx.isSelf
                ? button('<i class="fas fa-pencil-alt" aria-hidden="true"></i> Editar mis datos', function(event,captured){ProfessorProfile.openOwnProfileEdit(String(captured[0]));}, [professor.id], 'is-primary') : '';
        const contact = [
            professor.email ? `<a href="mailto:${Utils.sanitizeHTML(professor.email)}">${Utils.sanitizeHTML(professor.email)}</a>` : '',
            professor.phone ? `<a href="tel:${Utils.sanitizeHTML(String(professor.phone).replace(/\s+/g, ''))}">${Utils.sanitizeHTML(professor.phone)}</a>` : ''
        ].filter(Boolean);

        return `
            ${selfView ? '' : `<button type="button" class="ppf-back" ${UIEvents.attr("click", function(event,captured){App.navigate('profesores');}, [])}><i class="fas fa-chevron-left" aria-hidden="true"></i> Profesores</button>`}
            <div class="ppf-who">
                <button type="button" class="ppf-avatar" aria-label="Cambiar la foto de perfil" title="Cambiar la foto de perfil" ${UIEvents.attr("click", function(event,captured){document.getElementById("avatar-file-input-"+String(captured[0])).click();}, [professor.id])}>
                    ${professor.photo ? `<img ${StudentPhotos.attributes(professor.photo)} alt="">` : Utils.sanitizeHTML(initials)}
                    <span class="ppf-avatar-overlay" aria-hidden="true"><i class="fas fa-camera"></i></span>
                </button>
                <input type="file" id="avatar-file-input-${Utils.sanitizeHTML(professor.id)}" accept="image/png,image/jpeg,image/gif,image/webp" hidden ${UIEvents.attr("change", function(event,captured){ProfessorProfile.uploadAvatar(String(captured[0]),event);}, [professor.id])}>
                <div class="ppf-who-text">
                    <div class="ppf-eyebrow">${Utils.sanitizeHTML(professor.specialty || 'Profesor')}</div>
                    <h1 class="ppf-name">${Utils.sanitizeHTML(professor.firstName)} ${Utils.sanitizeHTML(professor.lastName)}</h1>
                </div>
            </div>
            <div class="ppf-pills">
                <span class="ppf-pill is-${employmentStatus}"><span class="ppf-dot" aria-hidden="true"></span>${statusLabel}</span>
                ${professor.active === false ? '<span class="ppf-pill is-blocked">Acceso bloqueado</span>' : ''}
                ${username ? `<span class="ppf-pill is-ghost">@${Utils.sanitizeHTML(username)}</span>` : ''}
                <span class="ppf-pill is-ghost">${fixed ? 'Sueldo fijo' : StaffPay.isFreelance(professor) ? 'Autónomo' : 'Por horas'}</span>
            </div>
            <div class="ppf-figures">
                ${figures.map(f => `<div class="ppf-figure"><span class="ppf-figure-num">${f.value}</span><span class="ppf-figure-label">${f.label}</span>${f.sub ? `<span class="ppf-figure-sub">${f.sub}</span>` : ''}</div>`).join('')}
            </div>
            ${this.renderScheduleSummary(professor)}
            ${contact.length ? `<div class="ppf-contact">${contact.join('')}</div>` : ''}
            <div class="ppf-id-actions">
                ${edit}
                <div class="ppf-id-row">
                    ${button('<i class="fas fa-address-card" aria-hidden="true"></i> Contacto', function(event,captured){ProfessorProfile.showContactInfoModal(String(captured[0]));}, [professor.id])}
                    ${button('<i class="fas fa-download" aria-hidden="true"></i> PDF', function(event,captured){ProfessorProfile.downloadPDF(String(captured[0]));}, [professor.id])}
                    ${AuthManager.isAdmin() || AuthManager.isRRHH() ? button('<i class="fas fa-trash-alt" aria-hidden="true"></i> Borrar', function(event,captured){ProfessorProfile.confirmDelete(String(captured[0]));}, [professor.id], 'is-danger') : ''}
                </div>
            </div>
        `;
    },

    /** A card of «Resumen»; the link opens its tab. */
    summaryCard(tab, title, body, link, extra = '') {
        return `<section class="ppf-card ${extra}" data-ppf-card="${tab}">
            <div class="ppf-card-head">
                <h2>${title}</h2>
                ${link ? `<button type="button" class="ppf-link" ${UIEvents.attr("click", function(event,captured){ProfessorProfile.switchTab(String(captured[0]));}, [tab])}>${link}</button>` : ''}
            </div>
            ${body}
        </section>`;
    },

    renderSummary(ctx) {
        const { professor, classes, formations, selfView } = ctx;
        const has = tab => ctx.tabIds.includes(tab);
        const dayName = d => d.toLocaleDateString('es-ES', { weekday: 'short', day: '2-digit', month: '2-digit' });
        const upcoming = this.upcomingSessions(classes);
        const progress = this.courseProgress(classes, this.selectedYearStart);
        const short = ['D', 'L', 'M', 'X', 'J', 'V', 'S'];
        const course = `${this.selectedYearStart}/${String(this.selectedYearStart + 1).slice(2)}`;
        const cards = [];

        const tiles = formations.map(f => {
            const row = progress.get(f.id) || { total: 0, done: 0, days: new Set() };
            const done = row.total ? Math.min(100, Math.round(row.done / row.total * 100)) : 0;
            const color = this.formationColor(f.id, classes);
            const days = [...row.days].sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7)).map(d => short[d]).join(', ');
            return `<div class="ppf-progress">
                <div class="ppf-progress-name"><span class="ppf-swatch" style="background:${color}"></span>${Utils.sanitizeHTML(f.name)}</div>
                <div class="ppf-bar" role="img" aria-label="${done} % de las horas impartidas"><span style="width:${done}%;background:${color}"></span></div>
                <div class="ppf-muted">${Math.round(row.done)} de ${Math.round(row.total)} h impartidas${days ? ` · ${days}` : ''}</div>
            </div>`;
        }).join('');
        const sessions = upcoming.map(({ date, offset, cls }) => {
            const subject = DataManager.getSubject(cls.subjectId), formation = DataManager.getFormation(cls.formationId);
            return `<li class="ppf-session">
                <span class="ppf-session-day">${offset === 0 ? 'Hoy' : offset === 1 ? 'Mañana' : Utils.sanitizeHTML(dayName(date))}</span>
                <span class="ppf-mono">${Utils.sanitizeHTML(cls.startTime)}–${Utils.sanitizeHTML(cls.endTime)}</span>
                <span class="ppf-session-what"><span class="ppf-swatch" style="background:${this.getColorForClass(cls)}"></span><strong>${Utils.sanitizeHTML(subject?.name || 'Asignatura')}</strong> · ${Utils.sanitizeHTML(formation?.name || '')}${cls.classroom ? ` · ${Utils.sanitizeHTML(cls.classroom)}` : ''}</span>
            </li>`;
        }).join('');
        cards.push(this.summaryCard('clases', `Clases · curso ${course}`, `
            ${tiles ? `<div class="ppf-progress-grid">${tiles}</div>` : '<p class="ppf-muted">No tiene clases asignadas en este curso.</p>'}
            <div class="ppf-mono-title">Próximas sesiones</div>
            ${sessions ? `<ul class="ppf-sessions">${sessions}</ul>` : '<p class="ppf-muted">No hay sesiones en los próximos días.</p>'}
        `, 'Abrir calendario completo', 'is-wide'));

        if (has('jornada')) cards.push(this.summaryCard('jornada', 'Jornada esta semana', this.renderWeekBars(professor), 'Calendario'));
        if (has('contratos')) cards.push(this.summaryCard('contratos', 'Contrato', this.renderContractSummary(professor), `Historial (${StaffPay.contractsOf(professor).filter(c => c.id).length})`));
        if (has('vacaciones')) cards.push(this.summaryCard('vacaciones', 'Vacaciones y permisos', '<div id="ppf-sum-leaves"><p class="ppf-muted" role="status">Cargando…</p></div>', 'Todas'));
        if (has('justificantes')) cards.push(this.summaryCard('justificantes', 'Justificantes', this.renderJustificantesSummary(professor), `Todos (${this.justificantesOf(professor).length})`));
        if (AuthManager.isAdmin() && !ctx.isSelf) cards.push(this.summaryCard('acceso', 'Acceso a la app', this.renderAccessSummary(professor), ''));

        return `
            ${selfView ? this.renderSelfHeader(ctx, upcoming) : ''}
            <div class="ppf-sum">${cards.join('')}</div>`;
    },

    /** The own ficha: a greeting, today's classes and the person's shortcuts. */
    renderSelfHeader(ctx, upcoming) {
        const { professor } = ctx;
        const today = upcoming.filter(s => s.offset === 0);
        const go = (text, icon, fn, args) => `<button type="button" class="ppf-shortcut" ${UIEvents.attr("click", fn, args)}><span class="ppf-shortcut-icon" aria-hidden="true"><i class="fas ${icon}"></i></span>${text}</button>`;
        const shortcuts = [
            ctx.tabIds.includes('vacaciones') ? go('Pedir vacaciones', 'fa-umbrella-beach', function(event,captured){ProfessorProfile.openLeaveForm(String(captured[0]),'request','vacation');}, [professor.id]) : '',
            go('Mis nóminas', 'fa-file-invoice-dollar', function(event,captured){App.navigate('nominas');}, []),
            go('Subir justificante', 'fa-file-medical', function(event,captured){App.navigate('justificantes');}, []),
            go('Mi calendario', 'fa-calendar-alt', function(event,captured){ProfessorProfile.switchTab('clases');}, [])
        ].filter(Boolean).join('');
        return `<div class="ppf-hello">
            <div>
                <div class="ppf-eyebrow">${Utils.sanitizeHTML(new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' }))}</div>
                <h2 class="ppf-hello-title">Hola, ${Utils.sanitizeHTML(professor.firstName)}</h2>
                <p class="ppf-muted">${today.length ? `Hoy tienes ${today.length} ${today.length === 1 ? 'clase' : 'clases'}: la primera a las ${Utils.sanitizeHTML(today[0].cls.startTime)}.` : 'Hoy no tienes clases.'}</p>
            </div>
            <div class="ppf-shortcuts">${shortcuts}</div>
        </div>`;
    },

    /** Each formation's hours in the course (1 Oct – 30 Sep), the ones already taught and its weekdays. */
    courseProgress(classes, yearStart) {
        const pad = n => String(n).padStart(2, '0');
        const iso = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
        const today = iso(new Date()), end = new Date(yearStart + 1, 8, 30), byFormation = new Map();
        for (let d = new Date(yearStart, 9, 1); d <= end; d.setDate(d.getDate() + 1)) {
            const day = iso(d);
            if (DataManager.isHoliday(day)) continue;
            for (const cls of this.getClassesForProfessorDay(classes, d.getFullYear(), d.getMonth(), d.getDate())) {
                const row = byFormation.get(cls.formationId) || { total: 0, done: 0, days: new Set() };
                const hours = Utils.calculateDuration(cls.startTime, cls.endTime);
                row.total += hours;
                if (day <= today) row.done += hours;
                row.days.add(cls.dayOfWeek);
                byFormation.set(cls.formationId, row);
            }
        }
        return byFormation;
    },

    /** The next sessions from now (today's still to finish first), up to two months ahead. */
    upcomingSessions(classes, count = 4) {
        const pad = n => String(n).padStart(2, '0'), now = new Date(), out = [];
        const time = `${pad(now.getHours())}:${pad(now.getMinutes())}`;
        for (let offset = 0; offset < 60 && out.length < count; offset++) {
            const date = new Date(now.getFullYear(), now.getMonth(), now.getDate() + offset);
            if (DataManager.isHoliday(`${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`)) continue;
            const list = this.getClassesForProfessorDay(classes, date.getFullYear(), date.getMonth(), date.getDate())
                .filter(cls => offset > 0 || String(cls.endTime) > time)
                .sort((a, b) => String(a.startTime).localeCompare(String(b.startTime)));
            for (const cls of list.slice(0, count - out.length)) out.push({ date, offset, cls });
        }
        return out;
    },

    /** One colour per formation: that of its first subject with one. */
    formationColor(formationId, classes) {
        const cls = classes.find(c => c.formationId === formationId && this.safeColor(DataManager.getSubject(c.subjectId)?.color));
        return cls ? this.getColorForClass(cls) : this.getColorForFormation(formationId);
    },

    /** This week's working hours, day by day (holidays and leave included). */
    renderWeekBars(professor) {
        const now = new Date(), monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (now.getDay() + 6) % 7);
        const days = Array.from({ length: 7 }, (_, i) => this.workDay(professor, new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + i)))
            .filter((info, i) => i < 5 || info.hours);
        const max = Math.max(1, ...days.map(d => d.hours));
        const letters = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];
        const tag = { holiday: 'Festivo', vacation: 'Vacaciones', unpaid: 'Permiso', 'off-contract': 'Sin contrato', rest: 'Libre' };
        const lines = this.scheduleLines(StaffPay.daysFor(professor.workSchedule, now));
        return `<div class="ppf-week" role="list">
                ${days.map((info, i) => `<div class="ppf-week-day is-${info.state}${info.today ? ' is-today' : ''}" role="listitem" aria-label="${Utils.sanitizeHTML(info.label)}">
                    <span class="ppf-week-bar"><span style="height:${info.hours ? Math.max(12, Math.round(info.hours / max * 100)) : 0}%"></span></span>
                    <span class="ppf-week-label">${letters[i]} · ${info.hours ? `${Number(info.hours.toFixed(2)).toLocaleString('es-ES')} h` : tag[info.state] || '—'}</span>
                </div>`).join('')}
            </div>
            ${lines.length ? `<p class="ppf-muted">${lines.map(l => `${l.days} ${Utils.sanitizeHTML(l.text)}`).join(' · ')}</p>` : ''}`;
    },

    /** The contract in force (or the next one): dates, pay and its notice to the advisory. */
    renderContractSummary(professor) {
        const contract = StaffPay.focusContract(professor);
        if (!contract) return '<p class="ppf-muted">Aún no tiene ningún contrato.</p>';
        const fixed = StaffPay.isFixed(professor);
        const fmt = iso => new Date(`${iso}T12:00:00`).toLocaleDateString('es-ES');
        const pay = fixed ? `${Utils.formatCurrency(contract.monthlySalary ?? StaffPay.monthlySalary(professor))} netos/mes`
            : `${Utils.formatCurrency(contract.hourlyRate ?? professor.hourlyRate)} /h brutos`;
        const irpf = professor.irpfRate != null ? ` · IRPF ${Number(professor.irpfRate).toLocaleString('es-ES', { maximumFractionDigits: 2 })} %` : '';
        // A ficha from before contracts existed sends the notice without a contract id.
        const canSend = this.canManageStaff() && !contract.freelance && !contract.advisoryNotified;
        return `
            <div class="ppf-strong">${contract.freelance ? 'Autónomo' : fixed ? 'Sueldo fijo' : 'Por horas'}${contract.workingTime ? ` · ${Utils.sanitizeHTML(contract.workingTime)}` : ''}</div>
            <div class="ppf-muted">${contract.endDate ? `${fmt(contract.startDate)} – ${fmt(contract.endDate)}` : `Desde ${fmt(contract.startDate)}`} · ${Utils.sanitizeHTML(pay)}${irpf}</div>
            <div class="ppf-inline">
                ${contract.freelance ? '<span class="ppf-pill is-ghost-dark">Sin alta en la asesoría</span>'
                    : `<span class="ppf-pill ${contract.advisoryNotified ? 'is-ok' : 'is-warn'}">${contract.advisoryNotified ? 'Asesoría avisada' : 'Asesoría sin avisar'}</span>`}
                ${canSend ? `<button type="button" class="ppf-btn is-small" ${UIEvents.attr("click", function(event,captured){ProfessorProfile.sendContractNotice(String(captured[0]),String(captured[1]),this);}, [professor.id, contract.id || ''])}>Enviar aviso</button>` : ''}
            </div>`;
    },

    /** «Contratos y nómina»: the pay of the ficha, then each contract (the list only for RRHH and admin). */
    renderContractsPane(ctx) {
        const { professor, stats, canSeeSalaries } = ctx;
        const fixed = StaffPay.isFixed(professor);
        const facts = canSeeSalaries ? [
            fixed ? ['Sueldo neto mensual', `${Utils.formatCurrency(StaffPay.monthlySalary(professor))} · 12 pagas`] : ['Tarifa por hora, bruta', Utils.formatCurrency(professor.hourlyRate)],
            [fixed ? 'Sueldo neto / mes' : 'Nómina bruta estimada / mes', Utils.formatCurrency(stats.monthlyPayroll)],
            [fixed ? 'Coste del curso' : 'Bruto estimado del curso', Utils.formatCurrency(stats.weeklyCost)],
            ['IRPF pedido', professor.irpfRate != null ? `${Number(professor.irpfRate).toLocaleString('es-ES', { maximumFractionDigits: 2 })} %` : 'El que corresponda']
        ] : [];
        const contract = StaffPay.focusContract(professor);
        const notice = !this.canManageStaff() && contract && !contract.freelance
            ? `<p class="ppf-note ${contract.advisoryNotified ? '' : 'is-warn'}">${contract.advisoryNotified ? 'La asesoría ya tiene el aviso del contrato en vigor.' : 'Falta avisar a la asesoría del contrato en vigor.'}</p>` : '';
        return `
            ${facts.length ? `<div class="ppf-card ppf-facts">${facts.map(([label, value]) => `<div><span class="ppf-muted">${label}</span><strong>${Utils.sanitizeHTML(value)}</strong></div>`).join('')}</div>` : ''}
            ${notice}
            ${this.renderContractsSection(professor)}`;
    },

    /** Admin: the account to sign in with, and the way to give or send it. */
    renderAccessSummary(professor) {
        const user = AuthManager.getUser(professor.id), username = this.accessUsername(professor);
        const action = (text, fn, args, cls = '') => `<button type="button" class="ppf-btn is-small ${cls}" ${UIEvents.attr("click", fn, args)}>${text}</button>`;
        if (!user) return `<p class="ppf-muted">Todavía no tiene cuenta para entrar en SICAF.</p>
            <div class="ppf-inline">${action('<i class="fas fa-key" aria-hidden="true"></i> Dar acceso', function(event,captured){ProfessorsView.openCreateAccess(String(captured[0]));}, [professor.id])}</div>`;
        return `<p>Usuario <strong>@${Utils.sanitizeHTML(username || user.username || '')}</strong>${professor.active === false ? ' · <span class="ppf-pill is-blocked">Acceso bloqueado</span>' : ''}</p>
            <div class="ppf-inline">
                ${user.role === 'professor' && professor.email ? action('<i class="fas fa-paper-plane" aria-hidden="true"></i> Enviar acceso por correo', function(event,captured){ProfessorsView.resendProfessorAccess(String(captured[0]));}, [professor.id]) : ''}
                ${action('Bloquear o dar módulos: «Editar ficha»', function(event,captured){ProfessorsView.editProfessor(String(captured[0]));}, [professor.id], 'is-ghost')}
            </div>`;
    },

    // ─── Justificantes of the person: whoever reviews them, and the person themself ───
    canSeeJustificantes(professor) {
        return typeof JustificantesView !== 'undefined' && (JustificantesView.isReviewer() || this.isOwnProfile(professor));
    },

    justificantesOf(professor) {
        if (typeof JustificantesView === 'undefined') return [];
        return JustificantesView.all().filter(doc => doc.professorId && String(doc.professorId) === String(professor.id))
            .sort((a, b) => new Date(b.date) - new Date(a.date));
    },

    justificanteRow(doc) {
        const pdf = JustificantesView.isPdf(doc);
        return `<li class="ppf-doc ${doc.reviewedAt ? '' : 'is-new'}">
            <span class="ppf-doc-ext ${pdf ? 'is-pdf' : ''}" aria-hidden="true">${pdf ? 'PDF' : 'IMG'}</span>
            <span class="ppf-doc-text"><strong>${Utils.sanitizeHTML(doc.fileName || 'Justificante')}</strong>
                <span class="ppf-muted">Subido el ${Utils.sanitizeHTML(JustificantesView.day(doc.date))}${doc.notes ? ` · ${Utils.sanitizeHTML(doc.notes)}` : ''}</span></span>
            <span class="ppf-pill ${doc.reviewedAt ? 'is-ok' : 'is-warn'}">${doc.reviewedAt ? 'Visto' : 'Sin abrir'}</span>
            <span class="ppf-inline">
                <button type="button" class="ppf-btn is-small" ${UIEvents.attr("click", function(event,captured){ProfessorProfile.openJustificante(String(captured[0]));}, [doc.id])}>Ver</button>
                <button type="button" class="ppf-btn is-small" ${UIEvents.attr("click", function(event,captured){JustificantesView.download(String(captured[0]));}, [doc.id])}>Descargar</button>
            </span>
        </li>`;
    },

    renderJustificantesSummary(professor) {
        const docs = this.justificantesOf(professor);
        if (!docs.length) return '<p class="ppf-muted">No ha enviado ningún justificante.</p>';
        const unseen = docs.filter(d => !d.reviewedAt).length;
        return `<ul class="ppf-docs">${docs.slice(0, 2).map(doc => this.justificanteRow(doc)).join('')}</ul>
            <p class="ppf-muted">${unseen ? `${unseen} sin abrir` : 'Todos vistos'}</p>`;
    },

    renderJustificantesPane(ctx) {
        const docs = this.justificantesOf(ctx.professor);
        const reviewer = JustificantesView.isReviewer();
        return `<section class="ppf-card">
            <div class="ppf-card-head">
                <h2>Justificantes</h2>
                <button type="button" class="ppf-btn is-small" ${UIEvents.attr("click", function(event,captured){App.navigate('justificantes');}, [])}>${reviewer && !ctx.isSelf ? 'Abrir Gestión de justificantes' : 'Subir justificante'}</button>
            </div>
            ${docs.length ? `<ul class="ppf-docs">${docs.map(doc => this.justificanteRow(doc)).join('')}</ul>`
                : `<p class="ppf-muted">${ctx.isSelf ? 'Todavía no has enviado ningún justificante.' : 'No ha enviado ningún justificante.'}</p>`}
        </section>`;
    },

    /** Opens it where it is reviewed (and so marked as seen), or the person's own list. */
    openJustificante(id) {
        if (JustificantesView.isReviewer()) JustificantesView.selectedId = id;
        App.navigate('justificantes');
    },

    /** «Resumen» and the badge once the leave is loaded: the next ones and what is to decide. */
    paintLeaveSummary(professorId) {
        const data = this._leaves;
        if (data?.professorId !== professorId) return;
        const { leaves, balance, canManage, isSelf } = data;
        const toDecide = canManage && !isSelf ? leaves.filter(l => l.status === 'pending').length : 0;
        this.setBadge('vacaciones', toDecide);
        this.setPending('vacaciones', toDecide ? `${toDecide} ${toDecide === 1 ? 'ausencia' : 'ausencias'} por decidir` : '');
        const box = document.getElementById('ppf-sum-leaves');
        if (!box) return;
        const today = new Date().toISOString().slice(0, 10);
        const fmt = iso => new Date(`${iso}T12:00:00`).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
        const kind = { vacation: 'Vacaciones', unpaid: 'Permiso sin sueldo' };
        const status = { pending: ['Pendiente', 'is-warn'], approved: ['Aprobada', 'is-ok'] };
        const next = leaves.filter(l => l.endDate >= today && status[l.status]).sort((a, b) => a.startDate.localeCompare(b.startDate)).slice(0, 3);
        SafeHTML.set(box, `
            <p class="ppf-muted"><strong class="ppf-ink">${balance.left}</strong> de ${balance.allowance} días de vacaciones disponibles en ${balance.year}</p>
            ${next.length ? `<ul class="ppf-leaves">${next.map(l => `<li class="ppf-leave ${l.status === 'pending' ? 'is-pending' : ''}">
                <span><strong>${kind[l.kind]}</strong> · ${fmt(l.startDate)}${l.endDate !== l.startDate ? ` – ${fmt(l.endDate)}` : ''} <span class="ppf-muted">· ${l.days} ${l.days === 1 ? 'día' : 'días'}</span></span>
                <span class="ppf-inline">
                    <span class="ppf-pill ${status[l.status][1]}">${status[l.status][0]}</span>
                    ${l.status === 'pending' && canManage && !isSelf ? `<button type="button" class="ppf-btn is-small is-primary" ${UIEvents.attr("click", function(event,captured){ProfessorProfile.decideLeave(String(captured[0]),String(captured[1]),true);}, [professorId, l.id])}>Aprobar</button>
                        <button type="button" class="ppf-btn is-small" ${UIEvents.attr("click", function(event,captured){ProfessorProfile.decideLeave(String(captured[0]),String(captured[1]),false);}, [professorId, l.id])}>Rechazar</button>` : ''}
                </span>
            </li>`).join('')}</ul>` : '<p class="ppf-muted">No tiene ausencias próximas.</p>'}`);
    },

    // Self-service edit: a professor may change their own personal/contact data
    // and password, never employment fields (rate, alta/baja) — those stay
    // admin/RRHH-only via ProfessorsView.editProfessor. Server enforces the
    // same restriction independently (server/security/professor-writes.js).
    openOwnProfileEdit(professorId) {
        const user = AuthManager.currentUser;
        if (!user || user.role !== 'professor' || user.professorId !== professorId) return;
        const professor = DataManager.getProfessor(professorId);
        if (!professor) return;
        ModalManager.show('Editar Mi Perfil', this.renderSelfEditForm(professor));
    },

    renderSelfEditForm(professor) {
        return `
            <div class="dnr-proposal professor-form-dnr">
                <form id="professor-self-form" class="form-container">
                    <input type="hidden" name="professorId" value="${Utils.sanitizeHTML(professor.id)}">

                    <div class="dnr-divider"><i class="fas fa-id-badge"></i> Datos Personales</div>
                    <div class="form-grid">
                        <div class="form-group">
                            <label class="form-label" for="self-firstName">Nombre *</label>
                            <input type="text" class="form-input" id="self-firstName" name="firstName" value="${Utils.sanitizeHTML(professor.firstName)}" required placeholder="Nombre">
                        </div>
                        <div class="form-group">
                            <label class="form-label" for="self-lastName">Apellidos *</label>
                            <input type="text" class="form-input" id="self-lastName" name="lastName" value="${Utils.sanitizeHTML(professor.lastName)}" required placeholder="Apellidos">
                        </div>
                        <div class="form-group">
                            <label class="form-label" for="self-specialty">Especialidad</label>
                            <input type="text" class="form-input" id="self-specialty" name="specialty" value="${Utils.sanitizeHTML(professor.specialty || '')}" placeholder="Ej: Dirección, Guion...">
                        </div>
                        <div class="form-group">
                            <label class="form-label" for="self-dni">DNI</label>
                            <input type="text" class="form-input" id="self-dni" name="dni" value="${Utils.sanitizeHTML(professor.dni || '')}" placeholder="DNI o NIE">
                        </div>
                        <div class="form-group">
                            <label class="form-label" for="self-ssNumber">Nº SS</label>
                            <input type="text" class="form-input" id="self-ssNumber" name="ssNumber" value="${Utils.sanitizeHTML(professor.ssNumber || '')}" placeholder="Número de Seguridad Social">
                        </div>
                    </div>

                    <div class="dnr-divider"><i class="fas fa-address-book"></i> Contacto y Ubicación</div>
                    <div class="form-grid">
                        <div class="form-group">
                            <label class="form-label" for="self-email">Email</label>
                            <input type="email" class="form-input" id="self-email" name="email" value="${Utils.sanitizeHTML(professor.email || '')}" placeholder="Correo electrónico">
                        </div>
                        <div class="form-group">
                            <label class="form-label" for="self-phone">Teléfono</label>
                            <input type="tel" class="form-input" id="self-phone" name="phone" value="${Utils.sanitizeHTML(professor.phone || '')}" placeholder="Teléfono">
                        </div>
                        <div class="form-group" style="grid-column: 1 / -1;">
                            <label class="form-label" for="self-address">Dirección</label>
                            <input type="text" class="form-input" id="self-address" name="address" value="${Utils.sanitizeHTML(professor.address || '')}" placeholder="Dirección completa">
                        </div>
                        <div class="form-group">
                            <label class="form-label" for="self-bankAccount">Nº de Cuenta</label>
                            <input type="text" class="form-input" id="self-bankAccount" name="bankAccount" value="${Utils.sanitizeHTML(professor.bankAccount || '')}" placeholder="IBAN">
                        </div>
                    </div>

                    <div class="dnr-form-section is-account">
                        <h4><i class="fas fa-lock"></i> Cambiar Contraseña</h4>
                        <p style="margin-top: 0;">Déjalo en blanco si no quieres cambiarla.</p>
                        <div class="form-grid">
                            <div class="form-group">
                                <label class="form-label" for="self-currentPassword">Contraseña actual</label>
                                <input type="password" class="form-input" id="self-currentPassword" name="currentPassword" autocomplete="current-password">
                            </div>
                            <div class="form-group">
                                <label class="form-label" for="self-newPassword">Nueva contraseña</label>
                                <input type="password" class="form-input" id="self-newPassword" name="newPassword" minlength="10" autocomplete="new-password" placeholder="Mínimo 10 caracteres...">
                            </div>
                            <div class="form-group">
                                <label class="form-label" for="self-confirmPassword">Confirmar nueva contraseña</label>
                                <input type="password" class="form-input" id="self-confirmPassword" name="confirmPassword" autocomplete="new-password">
                            </div>
                        </div>
                    </div>

                    <div class="form-actions">
                        <button type="button" class="btn btn-secondary" ${UIEvents.attr("click", function(event,captured){ModalManager.closeModal();}, [])}>Cancelar</button>
                        <button type="submit" class="btn btn-primary">Guardar Cambios</button>
                    </div>
                </form>
            </div>
        `;
    },

    async saveOwnProfile(e) {
        e.preventDefault();
        if (this._savingOwnProfile) return;
        const user = AuthManager.currentUser;
        const formData = new FormData(e.target);
        const professorId = formData.get('professorId');
        if (!user || user.role !== 'professor' || user.professorId !== professorId) return;

        const newPassword = formData.get('newPassword') || '';
        const confirmPassword = formData.get('confirmPassword') || '';
        const currentPassword = formData.get('currentPassword') || '';
        if (newPassword || confirmPassword || currentPassword) {
            if (newPassword.length < 10) { Utils.showToast('La nueva contraseña debe tener al menos 10 caracteres', 'warning'); return; }
            if (newPassword !== confirmPassword) { Utils.showToast('Las contraseñas no coinciden', 'warning'); return; }
            if (!currentPassword) { Utils.showToast('Introduce tu contraseña actual para cambiarla', 'warning'); return; }
        }

        const professorData = {
            firstName: formData.get('firstName'),
            lastName: formData.get('lastName'),
            specialty: formData.get('specialty'),
            dni: formData.get('dni'),
            ssNumber: formData.get('ssNumber'),
            email: formData.get('email'),
            phone: formData.get('phone'),
            address: formData.get('address'),
            bankAccount: formData.get('bankAccount')
        };

        this._savingOwnProfile = true;
        try {
            const token = AuthManager.getCsrfToken();
            const response = await fetch(`/api/professors/${encodeURIComponent(professorId)}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'X-SICAF-CSRF': token },
                body: JSON.stringify(professorData)
            });
            const result = await response.json();
            if (!response.ok || !result.success) {
                Utils.showToast(result.error || 'No se pudo guardar la ficha', 'error');
                return;
            }

            if (newPassword) {
                const passwordResponse = await fetch(`/api/users/${encodeURIComponent(user.id)}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json', 'X-SICAF-CSRF': AuthManager.getCsrfToken() },
                    body: JSON.stringify({ password: newPassword, currentPassword })
                });
                const passwordResult = await passwordResponse.json();
                if (!passwordResponse.ok || !passwordResult.success) {
                    Utils.showToast(passwordResult.error || 'Ficha guardada, pero no se pudo cambiar la contraseña', 'warning');
                    ProfessorService.cacheProfessor(result.professor);
                    ModalManager.closeModal();
                    if (App.currentView) App.navigate(App.currentView, false);
                    return;
                }
                AuthManager.storeSession(passwordResult);
            }

            ProfessorService.cacheProfessor(result.professor);
            Utils.showToast('Perfil actualizado correctamente', 'success');
            ModalManager.closeModal();
            if (App.currentView) App.navigate(App.currentView, false);
        } catch (error) {
            Utils.showToast(error.message || 'No se pudo conectar con el servidor', 'error');
        } finally {
            this._savingOwnProfile = false;
        }
    },

    // Pay type, first contract dates (new ficha only), pay, IRPF and working schedule: block 3 of the form.
    renderPayFields(professor) {
        const fixed = StaffPay.isFixed(professor), freelance = !fixed && StaffPay.isFreelance(professor);
        const kind = fixed ? 'fixed' : freelance ? 'freelance' : 'hourly';
        const summer = professor?.workSchedule?.summer || null;
        const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
        const pad = n => String(n).padStart(2, '0');
        const dateSelects = (name, value, label) => `
            <select class="form-input" name="${name}Day" aria-label="${label}: día">${Array.from({ length: 31 }, (_, i) => `<option value="${pad(i + 1)}" ${value?.slice(3) === pad(i + 1) ? 'selected' : ''}>${i + 1}</option>`).join('')}</select>
            <select class="form-input" name="${name}Month" aria-label="${label}: mes">${months.map((month, i) => `<option value="${pad(i + 1)}" ${value?.slice(0, 2) === pad(i + 1) ? 'selected' : ''}>${month}</option>`).join('')}</select>`;
        const toggle = UIEvents.attr("change", function(event,captured){ProfessorProfile.togglePayType(this.form);}, []);
        const kinds = [
            ['hourly', 'Por horas', 'Cobra las horas de clase a su tarifa. Nómina cada mes.'],
            ['fixed', 'Sueldo fijo', 'Sueldo neto al mes con jornada y vacaciones.'],
            ['freelance', 'Autónomo', 'Factura sus horas. Sin alta en la asesoría.']
        ];
        const money = (name, label, unit, value, extra = '') => `
            <label class="form-label" for="${name}">${label}</label>
            <span class="nf-unit"><input type="number" class="form-input" id="${name}" name="${name}" ${extra} value="${value}"><span aria-hidden="true">${unit}</span></span>`;
        return `
            <fieldset class="nf-kinds">
                <legend class="form-label">Tipo de pago</legend>
                ${kinds.map(([value, title, text]) => `<label class="nf-kind">
                    <input type="radio" name="payType" value="${value}" ${kind === value ? 'checked' : ''} ${toggle}>
                    <span class="nf-kind-head"><strong>${title}</strong><span class="nf-kind-dot" aria-hidden="true"></span></span>
                    <span class="nf-kind-text">${text}</span>
                </label>`).join('')}
            </fieldset>
            <div class="nf-grid">
                ${professor ? '' : `<div class="form-group">
                    <label class="form-label" for="hireDate">Fecha de alta</label>
                    <input type="date" class="form-input" id="hireDate" name="hireDate">
                </div>
                <div class="form-group">
                    <label class="form-label" for="terminationDate">Fecha de baja</label>
                    <input type="date" class="form-input" id="terminationDate" name="terminationDate">
                </div>`}
                <div class="form-group" data-pay="hourly freelance">
                    ${money('hourlyRate', 'Tarifa por hora, bruta', '€/h', Number(professor?.hourlyRate) || 0, 'step="0.01" min="0"')}
                </div>
                <div class="form-group" data-pay="fixed">
                    ${money('monthlySalary', 'Sueldo neto mensual', '€/mes', professor?.monthlySalary ?? '', 'step="0.01" min="0.01" required')}
                    <small class="staff-pay-help">12 pagas. Las clases que imparta no se pagan aparte.</small>
                </div>
                <div class="form-group" data-pay="hourly fixed">
                    ${money('irpfRate', 'IRPF pedido por la persona', '%', professor?.irpfRate ?? '', 'step="0.01" min="0" max="100" inputmode="decimal" placeholder="El que corresponda"')}
                    <small class="staff-pay-help">Solo si pide una retención concreta. Va con el aviso de alta.</small>
                </div>
                <div class="form-group" data-pay="fixed">
                    <label class="form-label" for="vacationDays">Vacaciones al año</label>
                    <span class="nf-unit"><input type="number" class="form-input" id="vacationDays" name="vacationDays" step="1" min="0" max="365" required value="${Number.isInteger(professor?.vacationDays) ? professor.vacationDays : StaffPay.DEFAULT_VACATION_DAYS}"><span aria-hidden="true">días</span></span>
                    <small class="staff-pay-help">Naturales, por año natural. Los que sobren valen hasta el 15 de enero.</small>
                </div>
            </div>
            <p class="nf-notice" data-pay="freelance"><i class="fas fa-circle-info" aria-hidden="true"></i> Autónomo: no se da de alta ni se avisa a la asesoría. Cada mes se sube su factura en Nóminas.</p>
            <div class="nf-schedule" data-pay="fixed">
                <div class="nf-schedule-head">
                    <strong>Horario habitual</strong>
                    <span class="staff-pay-help">Deja en blanco los días que no trabaja; la segunda franja es para la jornada partida.</span>
                </div>
                ${this.renderScheduleTable('r', professor?.workSchedule?.regular)}
                <label class="staff-pay-check"><input type="checkbox" name="summerEnabled" ${summer ? 'checked' : ''} ${toggle}> Tiene horario de verano</label>
                <div data-summer>
                    <div class="staff-pay-range">
                        <span>Desde</span>${dateSelects('summerFrom', summer?.from || '07-01', 'Inicio del horario de verano')}
                        <span>hasta</span>${dateSelects('summerTo', summer?.to || '08-31', 'Fin del horario de verano')}
                    </div>
                    <p class="staff-pay-help">Se repite cada año en esas fechas.</p>
                    ${this.renderScheduleTable('v', summer?.days)}
                </div>
            </div>`;
    },

    renderScheduleTable(prefix, days) {
        const time = (name, value, label) => `<input type="time" class="form-input" name="${name}" value="${Utils.sanitizeHTML(value || '')}" aria-label="${label}">`;
        return `
            <div class="staff-schedule nf-week">
                ${StaffPay.DAY_LABELS.map((label, i) => {
                    const day = days?.[i] || [];
                    return `<div class="nf-day"><span class="nf-day-name">${label}</span>${[0, 1].map(k =>
                        `<span class="nf-day-range">${time(`${prefix}${i}s${k}`, day[k]?.[0], `${label}, entrada ${k + 1}`)}<span aria-hidden="true">–</span>${time(`${prefix}${i}e${k}`, day[k]?.[1], `${label}, salida ${k + 1}`)}</span>`).join('')}</div>`;
                }).join('')}
            </div>
            <button type="button" class="btn btn-secondary staff-schedule-copy" ${UIEvents.attr("click", function(event,captured){ProfessorProfile.copyMondayToWeekdays(this.form,String(captured[0]));}, [prefix])}>
                <i class="fas fa-copy"></i> Copiar el lunes de martes a viernes
            </button>`;
    },

    // Hidden fields are disabled too, so they are neither validated nor sent.
    togglePayType(form) {
        if (!form?.elements.payType) return;
        const kind = form.elements.payType.value;
        form.querySelectorAll('[data-pay]').forEach(block => { block.hidden = !block.dataset.pay.split(' ').includes(kind); });
        form.querySelectorAll('[data-summer]').forEach(block => { block.hidden = !form.elements.summerEnabled.checked; });
        form.querySelectorAll('[data-pay] :is(input, select, button), [data-summer] :is(input, select, button)').forEach(field => {
            field.disabled = !!field.closest('[hidden]');
        });
        this.updateFormPreview(form);
    },

    copyMondayToWeekdays(form, prefix) {
        for (let day = 1; day <= 4; day++) for (const field of ['s0', 'e0', 's1', 'e1']) {
            form.elements[`${prefix}${day}${field}`].value = form.elements[`${prefix}0${field}`].value;
        }
    },

    readScheduleDays(form, prefix, season) {
        return StaffPay.DAY_LABELS.map((label, i) => {
            const ranges = [];
            for (const k of [0, 1]) {
                const start = form.elements[`${prefix}${i}s${k}`].value, end = form.elements[`${prefix}${i}e${k}`].value;
                if (!start && !end) continue;
                if (!start || !end || start >= end) throw new Error(`Revisa el horario ${season} del ${label.toLowerCase()}: cada franja necesita entrada y salida, y la salida va después de la entrada.`);
                ranges.push([start.slice(0, 5), end.slice(0, 5)]);
            }
            ranges.sort((a, b) => a[0].localeCompare(b[0]));
            if (ranges.length === 2 && ranges[0][1] > ranges[1][0]) throw new Error(`Las dos franjas del ${label.toLowerCase()} (horario ${season}) se solapan.`);
            return ranges;
        });
    },

    /** The pay fields of the form, ready to send. Throws an Error with the message to show. */
    readPayFields(form) {
        // An autónomo is paid per hour on invoices: no IRPF for the advisory.
        if (form.elements.payType.value === 'freelance') return { payType: 'hourly', freelance: true, hourlyRate: Math.max(0, parseFloat(form.elements.hourlyRate.value) || 0) };
        const irpfText = form.elements.irpfRate?.value.trim() ?? '';
        const irpfRate = irpfText === '' ? null : Math.round(parseFloat(irpfText.replace(',', '.')) * 100) / 100;
        if (irpfRate !== null && !(irpfRate >= 0 && irpfRate <= 100)) throw new Error('El IRPF va de 0 a 100 %.');
        return { irpfRate, freelance: false, ...this.readPayTypeFields(form) };
    },

    readPayTypeFields(form) {
        if (form.elements.payType.value !== 'fixed') return { payType: 'hourly', hourlyRate: Math.max(0, parseFloat(form.elements.hourlyRate.value) || 0) };
        const monthlySalary = Math.round(parseFloat(form.elements.monthlySalary.value) * 100) / 100;
        if (!(monthlySalary > 0)) throw new Error('Indica el sueldo neto mensual.');
        const regular = this.readScheduleDays(form, 'r', 'habitual');
        let summer = null;
        if (form.elements.summerEnabled.checked) {
            const e = form.elements;
            summer = { from: `${e.summerFromMonth.value}-${e.summerFromDay.value}`, to: `${e.summerToMonth.value}-${e.summerToDay.value}`, days: this.readScheduleDays(form, 'v', 'de verano') };
        }
        if (!regular.some(day => day.length)) throw new Error('Indica el horario habitual de la jornada.');
        const workSchedule = StaffPay.normalizeSchedule({ regular, summer });
        if (!workSchedule) throw new Error('Revisa las fechas del horario de verano: alguna no existe.');
        const vacationDays = Number(form.elements.vacationDays?.value ?? StaffPay.DEFAULT_VACATION_DAYS);
        if (!Number.isInteger(vacationDays) || vacationDays < 0 || vacationDays > 365) throw new Error('Los días de vacaciones van de 0 a 365.');
        return { payType: 'fixed', monthlySalary, workSchedule, vacationDays, contractType: null };
    },

    // Grouped for reading: consecutive days with the same hours share a line.
    scheduleLines(days) {
        const short = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];
        const lines = [];
        (days || []).forEach((day, i) => {
            if (!day.length) return;
            const text = day.map(([start, end]) => `${start}–${end}`).join(' · ');
            const last = lines[lines.length - 1];
            if (last && last.end === i - 1 && last.text === text) last.end = i;
            else lines.push({ start: i, end: i, text });
        });
        return lines.map(line => ({ days: line.start === line.end ? short[line.start] : `${short[line.start]}–${short[line.end]}`, text: line.text }));
    },

    renderScheduleSummary(professor) {
        const schedule = professor.workSchedule;
        if (!StaffPay.isFixed(professor) || !schedule?.regular) return '';
        const inSummer = StaffPay.isSummer(schedule, new Date());
        const months = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
        const date = value => `${Number(value.slice(3))} ${months[Number(value.slice(0, 2)) - 1]}`;
        return `
            <div class="professor-schedule">
                <div class="professor-schedule-title"><i class="fas fa-business-time"></i> Jornada${inSummer ? ' · horario de verano' : ''}</div>
                ${this.scheduleLines(StaffPay.daysFor(schedule, new Date())).map(line => `
                    <div class="professor-schedule-row"><span>${line.days}</span><span>${Utils.sanitizeHTML(line.text)}</span></div>`).join('')}
                ${schedule.summer ? `<div class="professor-schedule-note">${inSummer ? 'Horario habitual' : 'Horario de verano'}: ${inSummer ? `desde el ${date(schedule.summer.to)}` : `${date(schedule.summer.from)} – ${date(schedule.summer.to)}`}</div>` : ''}
            </div>`;
    },

    // ─── Calendario de jornada: each day's working hours in a week, a month or a year, with
    // the summer schedule when it applies and the centre's holidays (Ajustes → Calendario escolar). ───
    workDate: null,
    workView: 'month',
    _workProfessorId: null,
    WORK_VIEWS: [['week', 'Semana'], ['month', 'Mes'], ['year', 'Año']],

    renderWorkCalendar(professor) {
        if (!StaffPay.isFixed(professor) || !professor.workSchedule?.regular) return '';
        if (this._workProfessorId !== professor.id) {
            this.workDate = new Date();
            this.workView = 'month';
            this._workProfessorId = professor.id;
        }
        return `<section class="professor-calendar-section work-calendar" id="professor-work-calendar" aria-labelledby="work-calendar-title">${this.renderWorkCalendarBody(professor)}</section>`;
    },

    /** One day of the person's schedule: state, ranges, hours and a spoken description. */
    workDay(professor, date) {
        const pad = n => String(n).padStart(2, '0');
        const iso = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
        const schedule = professor.workSchedule, weekday = (date.getDay() + 6) % 7;
        const summer = StaffPay.isSummer(schedule, date);
        const ranges = (StaffPay.daysFor(schedule, date) || [])[weekday] || [];
        const employed = StaffPay.employedOn(professor, iso);
        // Approved leave replaces the day; a pending request is only marked.
        const leaves = this._leaves?.professorId === professor.id ? this._leaves.leaves : [];
        const leave = employed && leaves.find(l => l.status === 'approved' && l.startDate <= iso && l.endDate >= iso);
        const pending = employed && !leave && leaves.some(l => l.status === 'pending' && l.startDate <= iso && l.endDate >= iso);
        const state = !employed ? 'off-contract' : leave ? leave.kind : !ranges.length ? 'rest' : DataManager.isHoliday(iso) ? 'holiday' : 'work';
        let label = date.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
        if (state === 'work') label += `: ${ranges.map(([from, to]) => `de ${from} a ${to}`).join(' y ')}${summer ? ' (horario de verano)' : ''}`;
        else label += { holiday: ': festivo', 'off-contract': ': fuera del contrato', rest: ': no trabaja', vacation: ': vacaciones', unpaid: ': permiso sin sueldo' }[state];
        if (pending) label += ' (ausencia pendiente de aprobar)';
        const today = new Date();
        return { iso, state, summer, ranges, label, pending, hours: state === 'work' ? StaffPay.dayHours(ranges) : 0,
            today: date.toDateString() === today.toDateString() };
    },

    workCell(info, number, extra = '') {
        const body = (info.state === 'work' ? info.ranges.map(([from, to]) => `<span class="wc-range">${from}–${to}</span>`).join('')
            : info.state === 'holiday' ? '<span class="wc-tag">Festivo</span>'
            : info.state === 'vacation' ? '<span class="wc-tag">Vacaciones</span>'
            : info.state === 'unpaid' ? '<span class="wc-tag">Permiso</span>'
            : info.state === 'off-contract' ? '<span class="wc-tag">Sin contrato</span>' : '')
            + (info.pending ? '<span class="wc-tag wc-pending">Pendiente</span>' : '');
        return `<div class="wc-day is-${info.state}${info.state === 'work' && info.summer ? ' is-summer' : ''}${info.pending ? ' is-pending' : ''}${info.today ? ' is-today' : ''}" title="${Utils.sanitizeHTML(info.label)}" aria-label="${Utils.sanitizeHTML(info.label)}">
            <span class="wc-num">${number}</span>${body}${extra}</div>`;
    },

    /** Totals of a list of days, for the footer. */
    workTotals(days) {
        return days.reduce((t, d) => ({ hours: t.hours + d.hours, work: t.work + (d.state === 'work' ? 1 : 0), holidays: t.holidays + (d.state === 'holiday' ? 1 : 0) }),
            { hours: 0, work: 0, holidays: 0 });
    },

    renderWorkCalendarBody(professor) {
        const view = this.workView, anchor = this.workDate || new Date();
        const hoursText = h => `${Number(h.toFixed(2)).toLocaleString('es-ES')} h`;
        const weekdays = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
        const daysOfMonth = (year, month) => Array.from({ length: new Date(year, month + 1, 0).getDate() }, (_, i) => new Date(year, month, i + 1));
        let grid, label, days, period;
        if (view === 'week') {
            const monday = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate() - (anchor.getDay() + 6) % 7);
            const dates = Array.from({ length: 7 }, (_, i) => new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + i));
            days = dates.map(date => this.workDay(professor, date));
            const short = d => d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
            label = `${short(dates[0])} – ${short(dates[6])} ${dates[6].getFullYear()}`;
            period = 'esta semana';
            grid = `<div class="wc-grid wc-week">
                ${weekdays.map(d => `<div class="wc-weekday" aria-hidden="true">${d}</div>`).join('')}
                ${days.map((info, i) => this.workCell(info, dates[i].getDate(), info.hours ? `<span class="wc-hours">${hoursText(info.hours)}</span>` : '')).join('')}
            </div>`;
        } else if (view === 'year') {
            const year = anchor.getFullYear();
            days = [];
            const months = Array.from({ length: 12 }, (_, month) => {
                const dates = daysOfMonth(year, month), infos = dates.map(date => this.workDay(professor, date));
                days.push(...infos);
                const lead = (new Date(year, month, 1).getDay() + 6) % 7;
                const name = new Date(year, month, 1).toLocaleDateString('es-ES', { month: 'long' });
                return `<button type="button" class="wc-mini" aria-label="Ver ${name} de ${year}" ${UIEvents.attr("click", function(event,captured){ProfessorProfile.openWorkMonth(String(captured[0]),Number(captured[1]),Number(captured[2]));}, [professor.id, year, month])}>
                    <span class="wc-mini-head"><span>${name}</span><span>${hoursText(this.workTotals(infos).hours)}</span></span>
                    <span class="wc-mini-grid" aria-hidden="true">
                        ${'<i class="is-empty"></i>'.repeat(lead)}${infos.map(info => `<i class="is-${info.state}${info.state === 'work' && info.summer ? ' is-summer' : ''}${info.pending ? ' is-pending' : ''}${info.today ? ' is-today' : ''}"></i>`).join('')}
                    </span>
                </button>`;
            });
            label = String(year);
            period = `en ${year}`;
            grid = `<div class="wc-year">${months.join('')}</div>`;
        } else {
            const year = anchor.getFullYear(), month = anchor.getMonth();
            const dates = daysOfMonth(year, month);
            days = dates.map(date => this.workDay(professor, date));
            const lead = (new Date(year, month, 1).getDay() + 6) % 7;
            label = new Date(year, month, 1).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
            period = 'este mes';
            grid = `<div class="wc-grid">
                ${weekdays.map(d => `<div class="wc-weekday" aria-hidden="true">${d}</div>`).join('')}
                ${'<div class="wc-day is-empty" aria-hidden="true"></div>'.repeat(lead)}
                ${days.map((info, i) => this.workCell(info, dates[i].getDate())).join('')}
            </div>`;
        }
        const totals = this.workTotals(days);
        const nav = (aria, delta, html, cls = '') => `<button type="button" class="${cls}" ${aria ? `aria-label="${aria}"` : ''} ${UIEvents.attr("click", function(event,captured){ProfessorProfile.changeWorkMonth(String(captured[0]),Number(captured[1]));}, [professor.id, delta])}>${html}</button>`;
        const unit = { week: 'Semana', month: 'Mes', year: 'Año' }[view];
        return `
            <div class="wc-head">
                <h2 class="wc-title" id="work-calendar-title"><i class="fas fa-business-time"></i> Calendario de jornada</h2>
                <div class="wc-controls">
                    <div class="wc-views" role="group" aria-label="Vista del calendario">
                        ${this.WORK_VIEWS.map(([id, name]) => `<button type="button" aria-pressed="${id === view}" class="${id === view ? 'is-active' : ''}" ${UIEvents.attr("click", function(event,captured){ProfessorProfile.setWorkView(String(captured[0]),String(captured[1]));}, [professor.id, id])}>${name}</button>`).join('')}
                    </div>
                    <div class="wc-nav">
                        ${nav(`${unit} anterior`, -1, '<i class="fas fa-chevron-left"></i>')}
                        <span class="wc-month" aria-live="polite">${Utils.sanitizeHTML(label)}</span>
                        ${nav(`${unit} siguiente`, 1, '<i class="fas fa-chevron-right"></i>')}
                        ${nav('', 0, 'Hoy', 'wc-today')}
                    </div>
                </div>
            </div>
            ${grid}
            <div class="wc-foot">
                <span><strong>${hoursText(totals.hours)}</strong> ${period} · ${totals.work} ${totals.work === 1 ? 'día' : 'días'} de trabajo · ${totals.holidays} ${totals.holidays === 1 ? 'festivo' : 'festivos'}</span>
                <span class="wc-legend" aria-hidden="true"><span><i class="is-work"></i>Habitual</span>${professor.workSchedule.summer ? '<span><i class="is-summer"></i>Verano</span>' : ''}<span><i class="is-holiday"></i>Festivo</span><span><i class="is-vacation"></i>Vacaciones</span><span><i class="is-unpaid"></i>Permiso sin sueldo</span></span>
            </div>`;
    },

    redrawWorkCalendar(professorId) {
        const professor = DataManager.getProfessor(professorId);
        const section = document.getElementById('professor-work-calendar');
        if (professor && section) SafeHTML.set(section, this.renderWorkCalendarBody(professor));
    },

    /** delta: -1 / +1 week, month or year (as the view), 0 = today. */
    changeWorkMonth(professorId, delta) {
        const d = this.workDate || new Date();
        this.workDate = delta === 0 ? new Date()
            : this.workView === 'week' ? new Date(d.getFullYear(), d.getMonth(), d.getDate() + 7 * delta)
            : this.workView === 'year' ? new Date(d.getFullYear() + delta, d.getMonth(), 1)
            : new Date(d.getFullYear(), d.getMonth() + delta, 1);
        this.redrawWorkCalendar(professorId);
    },

    setWorkView(professorId, view) {
        if (!this.WORK_VIEWS.some(([id]) => id === view)) return;
        // Looking at this month or year: the week shown is today's, not the one of the 1st.
        const d = this.workDate || new Date(), today = new Date();
        if (view === 'week' && d.getFullYear() === today.getFullYear() && (this.workView === 'year' || d.getMonth() === today.getMonth())) this.workDate = today;
        this.workView = view;
        this.redrawWorkCalendar(professorId);
    },

    /** From the year view: open that month. */
    openWorkMonth(professorId, year, month) {
        this.workDate = new Date(year, month, 1);
        this.workView = 'month';
        this.redrawWorkCalendar(professorId);
    },

    // ─── Vacaciones y permisos (Jornada, fase 3; server/security/staff-leaves.js) ───
    _leaves: null,

    canManageStaff() {
        return AuthManager.isAdmin() || AuthManager.isRRHH() || AuthManager.hasModule('personal-laboral', 'manage');
    },

    /** «Contratos»: each hire with its dates, pay and notice to the advisory (RRHH and admin). */
    renderContractsSection(professor) {
        if (!this.canManageStaff()) return '';
        const id = professor.id, fixed = StaffPay.isFixed(professor);
        const pad = n => String(n).padStart(2, '0'), now = new Date();
        const today = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
        const fmt = iso => new Date(`${iso}T12:00:00`).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
        const action = (text, fn, args, cls = 'btn-secondary') => `<button type="button" class="btn btn-sm ${cls}" ${UIEvents.attr("click", fn, args)}>${text}</button>`;
        const contracts = StaffPay.contractsOf(professor).filter(c => c.id).reverse();
        const rows = contracts.map(c => {
            const state = c.startDate > today ? ['Próximo', 'is-pending'] : c.endDate && c.endDate < today ? ['Terminado', 'is-cancelled'] : ['En vigor', 'is-approved'];
            const pay = fixed ? (c.monthlySalary != null ? `${Utils.formatCurrency(c.monthlySalary)} /mes neto` : 'Sueldo de la ficha')
                : (c.hourlyRate != null ? `${Utils.formatCurrency(c.hourlyRate)} /h bruta` : 'Tarifa de la ficha');
            const docTitle = `Contrato · ${professor.firstName} ${professor.lastName} · ${fmt(c.startDate)}`;
            const buttons = [
                ...(c.documentFilePath ? [action('<i class="fas fa-file-pdf"></i> Ver contrato', function(event,captured){ContractDocs.view(String(captured[0]),String(captured[1]));}, [c.documentFilePath, docTitle]),
                    action('<i class="fas fa-download" aria-hidden="true"></i><span class="sr-only">Descargar contrato</span>', function(event,captured){ContractDocs.download(String(captured[0]));}, [c.documentFilePath])] : []),
                ...(c.freelance ? [] : c.advisoryNotified ? [action('No avisada', function(event,captured){ProfessorsView.unmarkAdvisory(String(captured[0]),String(captured[1]));}, [id, c.id])] : [action('<i class="fas fa-paper-plane"></i> Avisar a la asesoría', function(event,captured){ProfessorProfile.sendContractNotice(String(captured[0]),String(captured[1]),this);}, [id, c.id], 'btn-primary'),
                    action('Ya avisada', function(event,captured){ProfessorProfile.markContractNotified(String(captured[0]),String(captured[1]));}, [id, c.id])]),
                action('<i class="fas fa-pen"></i> Editar', function(event,captured){ProfessorProfile.openContractForm(String(captured[0]),String(captured[1]));}, [id, c.id]),
                action('<i class="fas fa-trash-alt" aria-hidden="true"></i><span class="sr-only">Borrar contrato</span>', function(event,captured){ProfessorProfile.deleteContract(String(captured[0]),String(captured[1]));}, [id, c.id])
            ];
            return `<li class="sl-item ${state[0] === 'Terminado' ? 'is-past' : ''}">
                <span class="sl-status ${state[1]}">${state[0]}</span>
                <span class="sl-dates">${fmt(c.startDate)} – ${c.endDate ? fmt(c.endDate) : 'sin fecha de baja'} <small>· ${Utils.sanitizeHTML(pay)}</small></span>
                ${c.freelance ? '<span class="sl-status is-cancelled">Autónomo · sin alta en la asesoría</span>'
                    : `<span class="sl-status ${c.advisoryNotified ? 'is-approved' : 'is-pending'}">${c.advisoryNotified ? 'Asesoría avisada' : 'Aviso a la asesoría pendiente'}</span>`}
                ${[c.contractKind, c.position, c.workingTime, c.grossMonthly != null ? `${Utils.formatCurrency(c.grossMonthly)} brutos/mes en contrato` : null].some(Boolean)
                    ? `<span class="sl-note">${Utils.sanitizeHTML([c.contractKind, c.position, c.workingTime, c.grossMonthly != null ? `${Utils.formatCurrency(c.grossMonthly)} brutos/mes en contrato` : null].filter(Boolean).join(' · '))}</span>`
                    : c.documentFilePath ? '' : '<span class="sl-note">Sin PDF del contrato</span>'}
                <span class="sl-actions">${buttons.join('')}</span>
            </li>`;
        }).join('');
        return `<section class="professor-calendar-section staff-leaves staff-contracts" id="professor-contracts" aria-labelledby="staff-contracts-title">
            <div class="wc-head">
                <h2 class="wc-title" id="staff-contracts-title"><i class="fas fa-file-contract"></i> Contratos</h2>
                <div class="sl-buttons">${action('<i class="fas fa-file-arrow-up"></i> Subir contrato (PDF)', function(event,captured){ContractDocs.pick(String(captured[0]));}, [id])}${action('<i class="fas fa-plus"></i> Nuevo contrato', function(event,captured){ProfessorProfile.openContractForm(String(captured[0]),'');}, [id], 'btn-primary')}</div>
            </div>
            ${rows ? `<ul class="sl-list">${rows}</ul>` : '<p class="sl-muted">Aún no tiene ningún contrato. Añade su alta con «Nuevo contrato».</p>'}
            ${this.renderIrpfLine(professor)}
        </section>`;
    },

    /** IRPF the person asked for (empty when none); it reaches the advisory with the hire notice. */
    renderIrpfLine(professor) {
        if (professor.irpfRate == null) return '';
        const rate = `${Number(professor.irpfRate).toLocaleString('es-ES', { maximumFractionDigits: 2 })} %`;
        return `<ul class="sl-list"><li class="sl-item">
                <span class="sl-status is-approved">IRPF</span>
                <span class="sl-dates">${Utils.sanitizeHTML(rate)} <small>· pedido por la persona, va con el aviso de alta</small></span>
            </li></ul>`;
    },

    openContractForm(professorId, contractId) {
        const professor = DataManager.getProfessor(professorId);
        if (!professor) return;
        const fixed = StaffPay.isFixed(professor), list = StaffPay.contractsOf(professor);
        const contract = contractId ? list.find(c => c.id === contractId) : null;
        // A new contract starts with the pay of the last one (it can be raised here).
        const last = list[list.length - 1];
        const pay = contract ? (fixed ? contract.monthlySalary : contract.hourlyRate) : (fixed ? last?.monthlySalary ?? professor.monthlySalary : last?.hourlyRate ?? professor.hourlyRate);
        const freelance = contract ? !!contract.freelance : !!last?.freelance;
        const name = `${professor.firstName} ${professor.lastName}`.trim();
        ModalManager.show(contract ? `Contrato · ${name}` : `Nuevo contrato · ${name}`, `
            <div class="dnr-proposal professor-form-dnr">
            <form id="contract-form" class="form-container">
                <div class="form-grid">
                    <div class="form-group">
                        <label class="form-label" for="contract-start">Fecha de alta</label>
                        <input type="date" class="form-input" id="contract-start" name="startDate" required value="${Utils.sanitizeHTML(contract?.startDate || '')}">
                    </div>
                    <div class="form-group">
                        <label class="form-label" for="contract-end">Fecha de baja</label>
                        <input type="date" class="form-input" id="contract-end" name="endDate" value="${Utils.sanitizeHTML(contract?.endDate || '')}">
                    </div>
                    ${fixed ? '' : `<div class="form-group">
                        <label class="form-label" for="contract-kind">Tipo</label>
                        <select class="form-input" id="contract-kind" name="freelance">
                            <option value="" ${freelance ? '' : 'selected'}>Contratado (nómina)</option>
                            <option value="1" ${freelance ? 'selected' : ''}>Autónomo (factura)</option>
                        </select>
                    </div>`}
                    <div class="form-group">
                        <label class="form-label" for="contract-pay">${fixed ? 'Sueldo neto mensual (€)' : 'Tarifa por hora, bruta (€)'}</label>
                        <input type="number" class="form-input" id="contract-pay" name="pay" min="0" step="0.01" inputmode="decimal" value="${pay != null ? Number(pay).toFixed(2) : ''}">
                    </div>
                </div>
                <p class="pf-hint">Sin fecha de baja, el contrato sigue abierto. Dos contratos no pueden solaparse.${contract ? '' : ' Si es contratado, el alta queda pendiente de avisar a la asesoría; si es autónomo, no.'}</p>
                <div class="form-actions">
                    <button type="button" class="btn btn-secondary" ${UIEvents.attr("click", function(event,captured){ModalManager.closeModal();}, [])}>Cancelar</button>
                    <button type="submit" class="btn btn-primary"><i class="fas fa-save"></i> Guardar</button>
                </div>
            </form>
            </div>`);
        document.getElementById('contract-form').addEventListener('submit', async event => {
            event.preventDefault();
            const form = event.target, button = form.querySelector('[type="submit"]');
            if (button.disabled) return;
            const value = form.elements.pay.value.trim();
            const data = { startDate: form.elements.startDate.value, endDate: form.elements.endDate.value || null,
                [fixed ? 'monthlySalary' : 'hourlyRate']: value === '' ? null : Math.round(parseFloat(value) * 100) / 100,
                ...(form.elements.freelance ? { freelance: form.elements.freelance.value === '1' } : {}) };
            button.disabled = true;
            try {
                if (contract) await ProfessorService.updateContract(contract.id, data);
                else await ProfessorService.createContract(professorId, data);
                ModalManager.closeModal();
                Utils.showToast(contract ? 'Contrato guardado.' : data.freelance ? 'Periodo de autónomo añadido.' : 'Contrato añadido. Recuerda avisar a la asesoría.', 'success');
                App.navigate(`profesor/${professorId}`);
            } catch (error) {
                Utils.showToast(error.message, 'error');
                button.disabled = false;
            }
        });
    },

    deleteContract(professorId, contractId) {
        const contract = StaffPay.contractsOf(DataManager.getProfessor(professorId)).find(c => c.id === contractId);
        if (!contract) return;
        const fmt = iso => new Date(`${iso}T12:00:00`).toLocaleDateString('es-ES');
        Utils.showConfirm(`¿Borrar el contrato del ${fmt(contract.startDate)}${contract.endDate ? ` al ${fmt(contract.endDate)}` : ''}? Sus nóminas y finiquitos no se tocan.`, async () => {
            try {
                await ProfessorService.deleteContract(contractId);
                Utils.showToast('Contrato borrado.', 'success');
                App.navigate(`profesor/${professorId}`);
            } catch (error) { Utils.showToast(error.message, 'error'); }
        });
    },

    async sendContractNotice(professorId, contractId, button) {
        if (button?.disabled) return;
        if (button) button.disabled = true;
        await ProfessorService.sendAdvisoryEmail(professorId, contractId || null);
        App.navigate(`profesor/${professorId}`);
    },

    async markContractNotified(professorId, contractId) {
        try {
            await ProfessorService.updateContract(contractId, { advisoryNotified: true });
            Utils.showToast('Marcado como avisado a la asesoría.', 'success');
            App.navigate(`profesor/${professorId}`);
        } catch (error) { Utils.showToast(error.message, 'error'); }
    },

    renderLeaveSection(professor) {
        if (!StaffPay.hasVacations(professor) || !professor.workSchedule?.regular) return '';
        const id = professor.id;
        // Loaded after the ficha is drawn; the section and the work calendar repaint then.
        setTimeout(() => this.loadLeaves(id), 0);
        return `<section class="professor-calendar-section staff-leaves" id="professor-leaves" aria-labelledby="staff-leaves-title">
            <h2 class="wc-title" id="staff-leaves-title"><i class="fas fa-umbrella-beach"></i> Vacaciones y permisos</h2>
            <p class="sl-muted" role="status">Cargando…</p>
        </section>`;
    },

    async loadLeaves(professorId) {
        const section = document.getElementById('professor-leaves');
        if (!section) return;
        try {
            const response = await fetch('/api/staff-leaves?professorId=' + encodeURIComponent(professorId), { cache: 'no-store' });
            const body = await response.json();
            if (!response.ok || !body.success) throw new Error(body.error);
            this._leaves = { professorId, ...body };
            this.renderLeaves(professorId);
            this.redrawWorkCalendar(professorId);
            this.paintLeaveSummary(professorId);
        } catch (e) {
            // No access (another person's private data) or no connection: the tab goes away quietly.
            if (document.getElementById('professor-leaves') === section) this.dropTab('vacaciones');
        }
    },

    renderLeaves(professorId) {
        const section = document.getElementById('professor-leaves'), data = this._leaves;
        if (!section || data?.professorId !== professorId) return;
        const { leaves, balance, canManage, isSelf } = data;
        const fmt = iso => new Date(`${iso}T12:00:00`).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
        const status = { pending: ['Pendiente', 'is-pending'], approved: ['Aprobada', 'is-approved'], rejected: ['Rechazada', 'is-rejected'], cancelled: ['Anulada', 'is-cancelled'] };
        const kind = { vacation: 'Vacaciones', unpaid: 'Permiso sin sueldo' };
        const action = (text, fn, args, cls = 'btn-secondary') => `<button type="button" class="btn btn-sm ${cls}" ${UIEvents.attr("click", fn, args)}>${text}</button>`;
        const today = new Date().toISOString().slice(0, 10);
        const ordered = [...leaves].sort((a, b) => (a.endDate < today) - (b.endDate < today) || (a.endDate < today ? b.startDate.localeCompare(a.startDate) : a.startDate.localeCompare(b.startDate)));
        const rows = ordered.map(l => {
            const [statusText, statusClass] = status[l.status];
            const buttons = [];
            if (l.status === 'pending' && canManage && !isSelf) {
                buttons.push(action('<i class="fas fa-check"></i> Aprobar', function(event,captured){ProfessorProfile.decideLeave(String(captured[0]),String(captured[1]),true);}, [professorId, l.id], 'btn-primary'));
                buttons.push(action('<i class="fas fa-xmark"></i> Rechazar', function(event,captured){ProfessorProfile.decideLeave(String(captured[0]),String(captured[1]),false);}, [professorId, l.id]));
            }
            if ((l.status === 'pending' && isSelf) || (['pending', 'approved'].includes(l.status) && canManage && !isSelf))
                buttons.push(action('Anular', function(event,captured){ProfessorProfile.cancelLeave(String(captured[0]),String(captured[1]));}, [professorId, l.id]));
            return `<li class="sl-item ${l.endDate < today ? 'is-past' : ''}">
                <span class="sl-kind is-${l.kind}">${kind[l.kind]}</span>
                <span class="sl-dates">${fmt(l.startDate)}${l.endDate !== l.startDate ? ` – ${fmt(l.endDate)}` : ''} <small>· ${l.days} ${l.days === 1 ? 'día' : 'días'}</small></span>
                <span class="sl-status ${statusClass}">${statusText}</span>
                ${l.note || l.decisionNote ? `<span class="sl-note">${l.note ? Utils.sanitizeHTML(l.note) : ''}${l.decisionNote ? `${l.note ? ' · ' : ''}<strong>Motivo:</strong> ${Utils.sanitizeHTML(l.decisionNote)}` : ''}</span>` : ''}
                ${buttons.length ? `<span class="sl-actions">${buttons.join('')}</span>` : ''}
            </li>`;
        }).join('');
        const open = (text, mode, kindValue, cls) => action(text, function(event,captured){ProfessorProfile.openLeaveForm(String(captured[0]),String(captured[1]),String(captured[2]));}, [professorId, mode, kindValue], cls);
        const buttons = isSelf
            ? open('<i class="fas fa-umbrella-beach"></i> Pedir vacaciones', 'request', 'vacation', 'btn-primary') + open('Pedir permiso sin sueldo', 'request', 'unpaid')
            : canManage ? open('<i class="fas fa-calendar-plus"></i> Asignar ausencia', 'assign', 'vacation', 'btn-primary') : '';
        SafeHTML.set(section, `
            <div class="wc-head">
                <h2 class="wc-title" id="staff-leaves-title"><i class="fas fa-umbrella-beach"></i> Vacaciones y permisos</h2>
                <div class="sl-buttons">${buttons}</div>
            </div>
            <p class="sl-balance"><strong>${balance.left}</strong> de ${balance.allowance} días de vacaciones disponibles en ${balance.year}${balance.carry ? ` · y <strong>${balance.carry}</strong> de ${balance.year - 1} hasta el 15 de enero` : ''}</p>
            ${rows ? `<ul class="sl-list">${rows}</ul>` : '<p class="sl-muted">No hay ausencias pedidas ni asignadas.</p>'}`);
    },

    openLeaveForm(professorId, mode, kindValue) {
        const professor = DataManager.getProfessor(professorId), data = this._leaves;
        if (!professor || data?.professorId !== professorId) return;
        const assign = mode === 'assign';
        const name = `${professor.firstName} ${professor.lastName}`.trim();
        ModalManager.show(assign ? `Asignar ausencia · ${name}` : kindValue === 'unpaid' ? 'Pedir permiso sin sueldo' : 'Pedir vacaciones', `
            <div class="dnr-proposal professor-form-dnr">
            <form id="leave-form" class="form-container">
                <div class="form-grid">
                    <div class="form-group">
                        <label class="form-label" for="leave-kind">Tipo</label>
                        <select class="form-input" id="leave-kind" name="kind">
                            <option value="vacation" ${kindValue === 'vacation' ? 'selected' : ''}>Vacaciones</option>
                            <option value="unpaid" ${kindValue === 'unpaid' ? 'selected' : ''}>Permiso sin sueldo</option>
                        </select>
                    </div>
                    <div class="form-group">
                        <label class="form-label" for="leave-from">Desde</label>
                        <input type="date" class="form-input" id="leave-from" name="startDate" required>
                    </div>
                    <div class="form-group">
                        <label class="form-label" for="leave-to">Hasta (incluido)</label>
                        <input type="date" class="form-input" id="leave-to" name="endDate" required>
                    </div>
                    <div class="form-group" style="grid-column: 1 / -1;">
                        <label class="form-label" for="leave-note">Nota (opcional)</label>
                        <input type="text" class="form-input" id="leave-note" name="note" maxlength="500">
                    </div>
                    <p class="pf-hint" id="leave-count" role="status" style="grid-column: 1 / -1;">Elige las fechas.</p>
                    <p class="pf-hint" style="grid-column: 1 / -1;">${assign ? 'Quedará aprobada al momento y se le avisará.' : 'RRHH o administración la revisará; recibirás un aviso con la respuesta.'} Los permisos sin sueldo descuentan su parte del sueldo de ese mes.</p>
                </div>
                <div class="form-actions">
                    <button type="button" class="btn btn-secondary" ${UIEvents.attr("click", function(event,captured){ModalManager.closeModal();}, [])}>Cancelar</button>
                    <button type="submit" class="btn btn-primary">${assign ? 'Asignar' : 'Enviar solicitud'}</button>
                </div>
            </form>
            </div>`);
        const form = document.getElementById('leave-form');
        const update = () => {
            const days = StaffPay.leaveDates(form.startDate.value, form.endDate.value).length, box = document.getElementById('leave-count');
            if (!box) return;
            if (!days) { box.textContent = form.startDate.value && form.endDate.value ? 'El final no puede ser anterior al inicio.' : 'Elige las fechas.'; return; }
            const draft = { kind: form.kind.value, status: 'pending', startDate: form.startDate.value, endDate: form.endDate.value };
            const left = form.kind.value === 'vacation' ? StaffPay.vacationLeft(professor, [...data.leaves, draft]) : null;
            const short = left && Object.entries(left).find(([, n]) => n < 0);
            box.textContent = `${days} ${days === 1 ? 'día natural' : 'días naturales'}` + (short ? ` · faltan ${-short[1]} días de vacaciones de ${short[0]}` : '');
        };
        for (const field of ['kind', 'startDate', 'endDate']) form[field].addEventListener('input', update);
        form.startDate.addEventListener('change', () => { if (!form.endDate.value || form.endDate.value < form.startDate.value) form.endDate.value = form.startDate.value; update(); });
        form.addEventListener('submit', async event => {
            event.preventDefault();
            const button = form.querySelector('[type="submit"]');
            if (button.disabled) return;
            button.disabled = true;
            try {
                const response = await fetch('/api/staff-leaves', { method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ professorId, kind: form.kind.value, startDate: form.startDate.value, endDate: form.endDate.value, note: form.note.value }) });
                const body = await response.json();
                if (!response.ok || !body.success) throw new Error(body.error || 'No se pudo guardar.');
                ModalManager.closeModal();
                Utils.showToast(assign ? 'Ausencia asignada.' : 'Solicitud enviada.', 'success');
                this.loadLeaves(professorId);
            } catch (e) {
                Utils.showToast(e.message, 'error');
                button.disabled = false;
            }
        });
    },

    async leaveAction(professorId, url, payload, done) {
        try {
            const response = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
            const body = await response.json();
            if (!response.ok || !body.success) throw new Error(body.error || 'No se pudo completar.');
            Utils.showToast(done, 'success');
            this.loadLeaves(professorId);
        } catch (e) {
            Utils.showToast(e.message, 'error');
        }
    },

    decideLeave(professorId, leaveId, approve) {
        if (approve) return this.leaveAction(professorId, `/api/staff-leaves/${encodeURIComponent(leaveId)}/decision`, { approve: true }, 'Ausencia aprobada.');
        ModalManager.show('Rechazar ausencia', `
            <div class="dnr-proposal professor-form-dnr">
            <form id="leave-reject-form" class="form-container">
                <div class="form-group">
                    <label class="form-label" for="leave-reason">Motivo (se le enviará en el aviso)</label>
                    <input type="text" class="form-input" id="leave-reason" name="reason" maxlength="500">
                </div>
                <div class="form-actions">
                    <button type="button" class="btn btn-secondary" ${UIEvents.attr("click", function(event,captured){ModalManager.closeModal();}, [])}>Cancelar</button>
                    <button type="submit" class="btn btn-danger">Rechazar</button>
                </div>
            </form>
            </div>`);
        document.getElementById('leave-reject-form').addEventListener('submit', event => {
            event.preventDefault();
            const reason = event.target.reason.value;
            ModalManager.closeModal();
            this.leaveAction(professorId, `/api/staff-leaves/${encodeURIComponent(leaveId)}/decision`, { approve: false, note: reason }, 'Ausencia rechazada.');
        });
    },

    cancelLeave(professorId, leaveId) {
        Utils.showConfirm('¿Anular esta ausencia? Sus días vuelven a quedar libres.', () =>
            this.leaveAction(professorId, `/api/staff-leaves/${encodeURIComponent(leaveId)}/cancel`, {}, 'Ausencia anulada.'));
    },

    renderForm(professor = null) {
        this._pendingPhoto = null;
        const directoryOnly = !AuthManager.isAdmin() && !AuthManager.isRRHH() && !AuthManager.hasModule('personal-laboral','manage');
        // Only admins load every account; for them a missing one means the person cannot sign in yet.
        const hasAccount = !!professor && (!AuthManager.isAdmin() || !!AuthManager.getUser(professor.id));
        const privateDisabled = directoryOnly ? 'disabled' : '';
        const hr = !directoryOnly;

        const initials = professor ? `${professor.firstName[0]}${professor.lastName[0]}`.toUpperCase() : '';
        const field = (name, label, value, attrs = '', extra = '') => `
            <div class="form-group ${extra}">
                <label class="form-label" for="${name}">${label}</label>
                <input class="form-input" id="${name}" name="${name}" value="${Utils.sanitizeHTML(value || '')}" ${attrs}>
            </div>`;
        const step = (n, title, id) => `<div class="nf-step"><span class="nf-num" aria-hidden="true">${n}</span><h3 id="${id}">${title}</h3></div>`;
        const update = UIEvents.attr("input", function(event,captured){ProfessorProfile.updateFormPreview(this);}, []);
        const changed = UIEvents.attr("change", function(event,captured){ProfessorProfile.updateFormPreview(this);}, []);

        return `
            <div class="dnr-proposal professor-form-dnr nf">
            <form id="professor-form" class="form-container" ${update} ${changed}>
                <input type="hidden" name="professorId" value="${Utils.sanitizeHTML(professor ? professor.id : '')}">
                <p class="nf-lead">Lo marcado con * es obligatorio. El resto se puede completar después en su ficha.</p>
                <div class="nf-layout">
                    <div class="nf-main">
                        <section class="nf-card" aria-labelledby="nf-s1">
                            ${step(1, 'Persona', 'nf-s1')}
                            ${directoryOnly ? '<p class="pf-hint pf-hint-top"><i class="fas fa-circle-info"></i> Coordinación solo puede editar nombre, apellidos y especialidad.</p>' : ''}
                            <div class="pf-grid pf-grid-photo nf-person">
                                <div class="pf-photo">
                                    <button type="button" class="pf-photo-btn ${professor ? '' : 'is-empty'}" id="photo-preview" ${directoryOnly ? 'disabled' : ''}
                                            aria-label="${directoryOnly ? 'Foto' : 'Cambiar la foto'}" title="${directoryOnly ? '' : 'Cambiar la foto'}"
                                            ${UIEvents.attr("click", function(event,captured){document.getElementById('pf-photo-input').click();}, [])}>
                                        ${professor && professor.photo ? `<img ${StudentPhotos.attributes(professor.photo)} alt="">` : (initials || '<i class="fas fa-camera"></i><span class="nf-photo-add">Añadir foto</span>')}
                                        ${directoryOnly ? '' : '<span class="pf-photo-overlay"><i class="fas fa-camera"></i> Cambiar</span>'}
                                    </button>
                                    ${directoryOnly ? '' : `<input type="file" id="pf-photo-input" hidden accept="image/png,image/jpeg,image/gif,image/webp" ${UIEvents.attr("change", function(event,captured){ProfessorProfile.pickFormPhoto(this);}, [])}>`}
                                </div>
                                ${field('firstName', 'Nombre *', professor?.firstName, 'type="text" required autocomplete="off"')}
                                ${field('lastName', 'Apellidos *', professor?.lastName, 'type="text" required autocomplete="off"')}
                                ${field('specialty', 'Especialidad', professor?.specialty, 'type="text"')}
                                <div class="nf-pair">
                                    ${field('dni', 'DNI / NIE', professor?.dni, `type="text" ${privateDisabled}`)}
                                    ${field('ssNumber', 'Nº Seg. Social', professor?.ssNumber, `type="text" ${privateDisabled}`)}
                                </div>
                            </div>
                            ${hr ? '<p class="nf-note">Con el DNI, sus nóminas y contratos en PDF se asignan solos a esta ficha.</p>' : ''}
                        </section>

                        <section class="nf-card" aria-labelledby="nf-s2">
                            ${step(2, 'Contacto', 'nf-s2')}
                            <div class="nf-grid">
                                ${field('email', 'Correo', professor?.email, `type="email" ${privateDisabled}`)}
                                ${field('phone', 'Teléfono', professor?.phone, `type="tel" ${privateDisabled}`)}
                                ${field('bankAccount', 'IBAN', professor?.bankAccount, `type="text" placeholder="ES00 0000 0000 0000 0000 0000" ${privateDisabled}`)}
                                ${field('address', 'Dirección', professor?.address, `type="text" placeholder="Calle, número, código postal y localidad" ${privateDisabled}`, 'is-full')}
                            </div>
                            ${hr ? '<p class="nf-note">El correo hace falta para darle acceso a SICAF como profesor.</p>' : ''}
                        </section>

                        ${hr ? `<section class="nf-card" aria-labelledby="nf-s3">
                            ${step(3, professor ? 'Pago y jornada' : 'Primer contrato y pago', 'nf-s3')}
                            ${this.renderPayFields(professor)}
                            <label class="nf-switch">
                                <input type="checkbox" name="active" ${professor?.active !== false ? 'checked' : ''}>
                                <span><strong>Acceso a la app habilitado</strong><small>Desactivarlo bloquea sus cuentas y sesiones, sin tocar el contrato.</small></span>
                            </label>
                            <p class="nf-note">${professor
                                ? 'Las altas y bajas van en «Contratos», en su ficha: puede tener varios contratos, cada uno con su tarifa. La tarifa de aquí es la del contrato vigente.'
                                : 'Las fechas son su primer contrato; los siguientes se añaden en «Contratos», en su ficha.'}</p>
                        </section>` : ''}

                        ${hasAccount ? `<section class="nf-card" aria-labelledby="nf-s4">
                            ${step(hr ? 4 : 3, 'Cuenta', 'nf-s4')}
                            <div class="form-group">
                                <label class="form-label" for="userPassword">Cambiar contraseña (déjalo vacío para mantenerla)</label>
                                <input type="password" class="form-input" id="userPassword" name="userPassword" autocomplete="new-password"
                                       minlength="10" ${AuthManager.isAdmin() ? '' : 'disabled'}
                                       placeholder="${AuthManager.isAdmin() ? 'Mínimo 10 caracteres' : 'Solo administración gestiona cuentas'}">
                            </div>
                        </section>` : ''}

                        ${professor && AuthManager.isAdmin() ? '<section class="dnr-form-section staff-module-access" id="staff-module-access" aria-label="Accesos adicionales"><p role="status">Cargando accesos adicionales…</p></section>' : ''}
                    </div>

                    <aside class="nf-aside">
                        <section class="nf-card nf-preview" aria-labelledby="nf-preview-title">
                            <h3 class="nf-mono-title" id="nf-preview-title">Así saldrá en la lista</h3>
                            <div class="nf-preview-person">
                                <span class="nf-preview-avatar" data-preview="initials">${Utils.sanitizeHTML(initials || '—')}</span>
                                <span class="nf-preview-text">
                                    <strong data-preview="name">${Utils.sanitizeHTML(professor ? `${professor.firstName} ${professor.lastName}`.trim() : 'Nombre y apellidos')}</strong>
                                    <span data-preview="specialty">${Utils.sanitizeHTML(professor?.specialty || 'Especialidad')}</span>
                                </span>
                            </div>
                            ${hr ? `<dl class="nf-preview-facts">
                                <div><dt>Pago</dt><dd data-preview="pay">—</dd></div>
                                <div><dt>Contrato</dt><dd data-preview="period">—</dd></div>
                                <div><dt>Situación</dt><dd><span class="nf-pill" data-preview="state">—</span></dd></div>
                            </dl>` : ''}
                        </section>
                        <section class="nf-card nf-after">
                            <h3 class="nf-title">${professor ? 'Al guardar' : 'Al crearla'}</h3>
                            <p>${hasAccount ? 'Si la persona tiene sesión abierta, verá los cambios al recargar.'
                                : 'No se envía nada a nadie. Cuando tú decidas, desde la lista de Profesores:'}</p>
                            ${hasAccount ? '' : `<ul>
                                ${hr ? '<li><strong>Avisar a la asesoría</strong> del alta, con su IRPF.</li>' : ''}
                                <li><strong>Darle acceso a SICAF</strong> con la llave verde: como profesor le llega por correo; como administración, RRHH, coordinación o almacén, la contraseña la pones tú.</li>
                            </ul>`}
                        </section>
                    </aside>
                </div>

                <div class="form-actions nf-actions">
                    <span class="nf-note">Más contratos, documentos y accesos extra, en su ficha.</span>
                    <button type="button" class="btn btn-secondary" ${UIEvents.attr("click", function(event,captured){ModalManager.closeModal();}, [])}>Cancelar</button>
                    <button type="submit" class="btn btn-primary">${professor ? 'Guardar cambios' : '<i class="fas fa-plus"></i> Crear persona'}</button>
                </div>
            </form>
            </div>
        `;
    },

    /** «Así saldrá en la lista»: the name, pay, first contract and situation as the form now stands. */
    updateFormPreview(form) {
        const box = form?.querySelector('.nf-preview');
        if (!box) return;
        const e = form.elements, set = (key, text) => { const el = box.querySelector(`[data-preview="${key}"]`); if (el) el.textContent = text; };
        const first = e.firstName.value.trim(), last = e.lastName.value.trim();
        set('name', `${first} ${last}`.trim() || 'Nombre y apellidos');
        set('initials', `${first[0] || ''}${last[0] || ''}`.toUpperCase() || '—');
        set('specialty', e.specialty.value.trim() || 'Especialidad');
        if (!e.payType) return;
        const kind = e.payType.value, amount = n => (Number(n) || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        set('pay', kind === 'fixed' ? `${amount(e.monthlySalary.value)} €/mes` : `${amount(e.hourlyRate.value)} €/h · ${kind === 'freelance' ? 'factura' : 'bruta'}`);
        // A new ficha takes its dates from the form; an existing one keeps its current contract.
        const professor = e.professorId.value ? DataManager.getProfessor(e.professorId.value) : null;
        const contract = professor ? StaffPay.focusContract(professor) : null;
        const start = e.hireDate ? e.hireDate.value : contract?.startDate || '', end = e.terminationDate ? e.terminationDate.value : contract?.endDate || '';
        const fmt = iso => new Date(`${iso}T12:00:00`).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit' });
        set('period', start ? (end ? `${fmt(start)} – ${fmt(end)}` : `desde ${fmt(start)}`) : 'Sin fechas');
        const today = new Date().toISOString().slice(0, 10);
        const days = start ? Math.round((new Date(`${start}T12:00:00`) - new Date(`${today}T12:00:00`)) / 86400000) : null;
        const state = !start || (end && end < today) ? ['Sin contrato', 'is-off'] : days > 0 ? [`Próximo · ${days} ${days === 1 ? 'día' : 'días'}`, 'is-soon'] : ['En alta', 'is-on'];
        const pill = box.querySelector('[data-preview="state"]');
        if (pill) { pill.textContent = state[0]; pill.className = `nf-pill ${state[1]}`; }
    },

    /** «Clases»: the course month by month (months with classes), for one formation at a time. */
    renderClassesPane(ctx) {
        const { professor, classes, formations } = ctx;
        const professorId = professor.id;
        const currentFormation = this.selectedFormationId ? DataManager.getFormation(this.selectedFormationId) : null;
        const formationName = currentFormation ? currentFormation.name : 'Programa académico';
        return `
            <div class="professor-calendar-section ppf-calendar" id="professor-calendar-module">
                <!-- Print Header Banner -->
                <div class="print-only-header">
                    <div>
                        <h1 class="print-school-name">${Utils.sanitizeHTML([SchoolService.get('shortName'), SchoolService.get('name')].filter(Boolean).join(' - ') || SchoolService.name())}</h1>
                        <h2 class="print-formation-name">Docente: ${Utils.sanitizeHTML(professor.firstName)} ${Utils.sanitizeHTML(professor.lastName)}</h2>
                        <p class="print-course-year">Calendario Académico · ${Utils.sanitizeHTML(formationName)} (${this.selectedYearStart} / ${this.selectedYearStart + 1})</p>
                    </div>
                    ${SchoolService.logoHtml(['logoUrl', 'emailLogoUrl'], 'class="print-logo"')}
                </div>

                <div class="ppf-cal-bar">
                    ${this.renderFormationTabs(professorId, formations)}
                    <div class="ppf-cal-tools">
                        <label for="ppf-course">Curso</label>
                        <select id="ppf-course" class="ppf-select" ${UIEvents.attr("change", function(event,captured){ProfessorProfile.changeAcademicYear(String(captured[0]),this.value);}, [professorId])}>
                            ${this.courseYearOptions(classes)}
                        </select>
                        <button type="button" class="ppf-icon-btn download-pdf-btn" aria-label="Descargar el calendario en PDF" title="Descargar PDF" ${UIEvents.attr("click", function(event,captured){ProfessorProfile.printCalendar();}, [])}>
                            <i class="fas fa-download" aria-hidden="true"></i>
                        </button>
                        <button type="button" class="ppf-icon-btn expand-module-btn" aria-label="Ampliar o reducir el calendario" title="Ampliar / reducir" ${UIEvents.attr("click", function(event,captured){ProfessorProfile.toggleExpand();}, [])}>
                            <i class="fas fa-expand" aria-hidden="true"></i>
                        </button>
                    </div>
                </div>
                <h2 class="ppf-cal-title" id="calendar-title">Calendario académico · ${Utils.sanitizeHTML(formationName)}</h2>

                <div class="academic-rows-container">
                    ${this.renderAcademicYearRows(professorId, this.selectedFormationId)}
                </div>
                <p class="ppf-legend" aria-hidden="true">
                    <span><i class="is-class"></i>Color de la asignatura</span>
                    <span><i class="is-many"></i>Varias asignaturas</span>
                    <span><i class="is-holiday"></i>Festivo</span>
                    <span><i class="is-today"></i>Hoy</span>
                    <span>Pulsa un día para ver sus sesiones.</span>
                </p>
            </div>`;
    },

    /** The courses to choose: the current one and every one with classes. */
    courseYearOptions(classes) {
        const currentToday = new Date();
        const activeYearStart = currentToday.getMonth() >= 8 ? currentToday.getFullYear() : currentToday.getFullYear() - 1;
        const yearsSet = new Set([activeYearStart, ...((typeof FileStorage !== 'undefined' && FileStorage.classYears) || [])]);
        classes.forEach(c => {
            if (c.startDate) {
                const parts = c.startDate.split('-');
                if (parts.length >= 2) {
                    const cYear = parseInt(parts[0], 10);
                    const cMonth = parseInt(parts[1], 10);
                    yearsSet.add(cMonth >= 10 ? cYear : cYear - 1);
                }
            }
        });
        return [...yearsSet].sort((a, b) => b - a)
            .map(y => `<option value="${y}" ${y === this.selectedYearStart ? 'selected' : ''}>${y} / ${y + 1}</option>`).join('');
    },

    renderFormationTabs(professorId, formations) {
        if (formations.length <= 1) return '';
        const classes = DataManager.getClassesByProfessor(professorId);
        return `
            <div class="ppf-fchips professor-tabs-container" role="group" aria-label="Formación">
                ${formations.map(f => {
                    const on = this.selectedFormationId === f.id;
                    return `<button type="button" class="ppf-fchip ${on ? 'is-on' : ''}" aria-pressed="${on}"
                            ${UIEvents.attr("click", function(event,captured){ProfessorProfile.switchFormation(String(captured[0]),String(captured[1]));}, [professorId, f.id])}>
                        <span class="ppf-swatch" style="background:${this.formationColor(f.id, classes)}"></span>${Utils.sanitizeHTML(f.name)}
                    </button>`;
                }).join('')}
            </div>
        `;
    },

    switchFormation(professorId, formationId) {
        this.selectedFormationId = formationId;
        App.navigate(`profesor/${professorId}`);
    },

    toggleExpand() {
        const module = document.getElementById('professor-calendar-module');
        const btn = module.querySelector('.expand-module-btn i');

        if (module.classList.toggle('is-expanded')) {
            btn.classList.replace('fa-expand', 'fa-compress');
            document.body.style.overflow = 'hidden'; // Prevent background scrolling
        } else {
            btn.classList.replace('fa-compress', 'fa-expand');
            document.body.style.overflow = '';
        }
    },

    printCalendar() {
        Utils.showToast('Preparando PDF para impresión...', 'info');

        // Small delay to let the toast appear and ensure UI is stable
        setTimeout(() => {
            window.print();
        }, 500);
    },

    renderAcademicYearRows(professorId, formationId = null) {
        if (!this.selectedYearStart) {
            const today = new Date();
            this.selectedYearStart = today.getMonth() >= 8 ? today.getFullYear() : today.getFullYear() - 1;
        }
        const yearStart = this.selectedYearStart;

        const months = [
            { month: 9, year: yearStart },   // Oct
            { month: 10, year: yearStart },  // Nov
            { month: 11, year: yearStart },  // Dec
            { month: 0, year: yearStart + 1 }, // Jan
            { month: 1, year: yearStart + 1 }, // Feb
            { month: 2, year: yearStart + 1 }, // Mar
            { month: 3, year: yearStart + 1 }, // Apr
            { month: 4, year: yearStart + 1 }, // May
            { month: 5, year: yearStart + 1 }, // Jun
            { month: 6, year: yearStart + 1 }, // Jul
            { month: 7, year: yearStart + 1 }, // Aug
            { month: 8, year: yearStart + 1 }  // Sep
        ];

        const allProfessorClasses = DataManager.getClassesByProfessor(professorId);

        // Filter classes by selected formation if specified
        const filteredClasses = formationId
            ? allProfessorClasses.filter(c => c.formationId === formationId)
            : allProfessorClasses;

        const activeMonths = months.filter(m => {
            const monthClasses = this.filterClassesByMonth(filteredClasses, m.year, m.month);
            return monthClasses.length > 0;
        });

        if (activeMonths.length === 0) {
            return `
                <div class="empty-state-message" style="text-align: center; padding: 3rem; color: var(--text-secondary); background: var(--background-secondary); border-radius: 8px; margin: 1rem 0;">
                    <i class="fas fa-calendar-times" style="font-size: 2rem; margin-bottom: 1rem; color: var(--text-tertiary);"></i>
                    <p style="font-size: 1.1rem;">No hay clases asignadas para este periodo.</p>
                </div>
            `;
        }

        return activeMonths.map(m => {
            const monthClasses = this.filterClassesByMonth(filteredClasses, m.year, m.month);

            return `
                <div class="academic-month-row">
                    <!-- Left: Mini Calendar -->
                    <div class="academic-month-calendar">
                        ${this.renderAcademicMonth(m.year, m.month, professorId, formationId)}
                    </div>
                    
                    <!-- Right: Sessions for this month -->
                    <div class="academic-month-sessions">
                        ${this.renderProfessorSessionsList(monthClasses)}
                    </div>
                </div>
            `;
        }).join('');
    },

    filterClassesByMonth(classes, year, month) {
        const monthStart = new Date(year, month, 1);
        const monthEnd = new Date(year, month + 1, 0, 23, 59, 59, 999);

        return classes.filter(cls => {
            const clsStart = new Date(cls.startDate);
            const clsEnd = cls.endDate ? new Date(cls.endDate) : null;

            // Simplified: if the class range overlaps with this month
            const overlaps = clsStart <= monthEnd && (!clsEnd || clsEnd >= monthStart);
            return overlaps;
        });
    },

    renderAcademicMonth(year, month, professorId, formationId = null) {
        const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
            'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        let startDayOfWeek = firstDay.getDay();

        // Adjust for Monday start (0 = Monday, 6 = Sunday)
        startDayOfWeek = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1;

        let html = `
            <div class="mini-month-card">
                <h4 class="mini-month-title">${monthNames[month]} ${year}</h4>
                <div class="mini-calendar-grid">
        `;

        // Headers
        ['L', 'M', 'X', 'J', 'V', 'S', 'D'].forEach(day => {
            html += `<div class="mini-calendar-header">${day}</div>`;
        });

        // Empty cells
        for (let i = 0; i < startDayOfWeek; i++) {
            html += '<div class="mini-calendar-day empty"></div>';
        }

        const allProfessorClasses = DataManager.getClassesByProfessor(professorId);
        const classes = formationId ? allProfessorClasses.filter(c => c.formationId === formationId) : allProfessorClasses;

        // Days of month
        for (let day = 1; day <= daysInMonth; day++) {
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const isHoliday = DataManager.isHoliday(dateStr);
            const dayClasses = this.getClassesForProfessorDay(classes, year, month, day);
            const classNames = ['mini-calendar-day'];
            let style = '';

            if (isHoliday) {
                classNames.push('is-holiday');
            }

            if (dayClasses.length > 0) {
                classNames.push('has-classes');

                // If it's a holiday, we might want to show classes too, 
                // but usually holidays mean no classes. 
                // We'll apply class background only if it's NOT a holiday
                // or use a subtle indicator if needed.
                if (!isHoliday) {
                    if (dayClasses.length === 1) {
                        const color = this.getColorForClass(dayClasses[0]);
                        style = `background-color: ${color}; color: white;`;
                    } else {
                        // Gradient for multi-formation days
                        const colors = [...new Set(dayClasses.map(c => this.getColorForClass(c)))];
                        const percent = 100 / colors.length;
                        const gradient = colors.map((c, i) => `${c} ${i * percent}% ${(i + 1) * percent}%`).join(', ');
                        style = `background: linear-gradient(135deg, ${gradient}); color: white;`;
                    }
                }
            }

            // Today
            const isToday = new Date().toDateString() === new Date(year, month, day).toDateString();
            if (isToday) classNames.push('today');

            html += `
                <div class="${classNames.join(' ')}" 
                     style="${style}"
                     ${UIEvents.attr("click", function(event,captured){ProfessorProfile.showDayDetails(captured[0],captured[1],captured[2],String(captured[3]));}, [year, month, day, professorId])}
                     title="${dayClasses.length > 0 ? Utils.sanitizeHTML(dayClasses.map(c => DataManager.getSubject(c.subjectId)?.name).join(', ')) : ''}">
                    ${day}
                </div>
            `;
        }

        html += '</div></div>';
        return html;
    },

    getClassesForProfessorDay(classes, year, month, day) {
        const targetDate = new Date(year, month, day);
        const targetDayOfWeek = targetDate.getDay();

        // Helper: parse "YYYY-MM-DD" as LOCAL time (not UTC) to avoid timezone shift
        const parseLocalDate = (dateStr) => {
            if (!dateStr) return null;
            const parts = dateStr.split(/[-T]/);
            return new Date(
                parseInt(parts[0]),
                parseInt(parts[1]) - 1,
                parseInt(parts[2])
            );
        };

        return classes.filter(cls => {
            // Check if day of week matches
            if (cls.dayOfWeek !== targetDayOfWeek) return false;

            // Date range (parsed in local time to avoid UTC offset shifting the date)
            const start = parseLocalDate(cls.startDate);
            if (!start) return false;
            start.setHours(0, 0, 0, 0);

            const end = cls.endDate ? parseLocalDate(cls.endDate) : null;
            if (end) end.setHours(23, 59, 59, 999);

            const target = new Date(targetDate);
            target.setHours(12, 0, 0, 0);

            if (target < start) return false;
            if (end && target > end) return false;

            return true;
        });
    },

    renderProfessorSessionsList(classes) {
        if (classes.length === 0) {
            return '<p style="text-align: center; color: var(--text-secondary); padding: 2rem;">No hay clases asignadas.</p>';
        }

        const grouped = this.groupClassesBySchedule(classes);

        return `
            <div class="compact-sessions-container">
                <div class="compact-sessions-header">
                    <div class="col-header">COL</div>
                    <div class="col-header">ASIGNATURA Y FORMACIÓN</div>
                    <div class="col-header">TIPO</div>
                    <div class="col-header">OBS.</div>
                </div>
                
                <div class="compact-sessions-list">
                    ${grouped.map(group => {
            const subject = DataManager.getSubject(group.subjectId);
            const formation = DataManager.getFormation(group.formationId);
            const color = this.safeColor(subject?.color) || this.getColorForFormation(group.formationId);
            const type = subject?.type || 'COMÚN';
            const daysMap = ['D', 'L', 'M', 'X', 'J', 'V', 'S'];
            const daysList = group.days.map(d => daysMap[d]).join(', ');

            return `
                            <div class="compact-session-row">
                                <div class="session-col">
                                    <div class="color-dot" style="background-color: ${color};"></div>
                                </div>
                                <div class="session-info">
                                    <span class="subject-name">${Utils.sanitizeHTML(subject?.name || 'Asignatura')}</span>
                                    <span class="formation-name-sep">·</span>
                                    <span class="formation-name">${Utils.sanitizeHTML(formation?.name || '---')}</span>
                                </div>
                                <div class="session-type">
                                    <span class="type-badge">${Utils.sanitizeHTML(type)}</span>
                                </div>
                                <div class="session-obs">
                                    <span class="days-text">${daysList}</span>
                                    <span class="time-text">${group.startTime} - ${group.endTime}</span>
                                </div>
                            </div>
                        `;
        }).join('')}
                </div>
            </div>
        `;
    },

    groupClassesBySchedule(classes) {
        const groups = {};

        classes.forEach(cls => {
            const key = `${cls.subjectId}-${cls.formationId}-${cls.startTime}-${cls.endTime}`;

            if (!groups[key]) {
                groups[key] = {
                    subjectId: cls.subjectId,
                    formationId: cls.formationId,
                    startTime: cls.startTime,
                    endTime: cls.endTime,
                    days: [],
                    classroom: cls.classroom,
                    observations: cls.observations || ''
                };
            }

            if (!groups[key].days.includes(cls.dayOfWeek)) {
                groups[key].days.push(cls.dayOfWeek);
            }
        });

        return Object.values(groups).map(group => {
            group.days.sort((a, b) => a - b);
            group.daysText = this.formatDaysRange(group.days);
            return group;
        });
    },

    formatDaysRange(days) {
        if (days.length === 0) return '';
        if (days.length === 1) return Utils.getDayName(days[0]);

        const isConsecutive = days.every((day, i) =>
            i === 0 || day === days[i - 1] + 1 || (days[i - 1] === 0 && day === 1)
        );

        if (isConsecutive && days.length > 2) {
            return `${Utils.getDayName(days[0])} a ${Utils.getDayName(days[days.length - 1])}`;
        }

        return days.map(d => Utils.getDayName(d)).join(', ');
    },

    showDayDetails(year, month, day, professorId) {
        const classes = DataManager.getClassesByProfessor(professorId);
        const dayClasses = this.getClassesForProfessorDay(classes, year, month, day);
        const date = new Date(year, month, day);

        const dayNames = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
        const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
            'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

        let content = `
            <div style="margin-bottom: 1.5rem; text-align: center;">
                <h3 style="font-size: 1.25rem; font-weight: 700;">${dayNames[date.getDay()]} ${day} de ${monthNames[month]} ${year}</h3>
            </div>
        `;

        if (dayClasses.length === 0) {
            content += '<p style="text-align: center; color: var(--text-secondary); padding: 2rem;">No hay clases programadas.</p>';
        } else {
            content += dayClasses.map(cls => {
                const subject = DataManager.getSubject(cls.subjectId);
                const formation = DataManager.getFormation(cls.formationId);
                const color = this.getColorForClass(cls);

                return `
                    <div class="day-class-detail" style="border-left: 4px solid ${color}; padding: 1rem; margin-bottom: 1rem; background: #f8fafc; border-radius: 8px;">
                        <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 0.5rem;">
                            <strong style="font-size: 1.125rem;">${Utils.sanitizeHTML(cls.startTime)} - ${Utils.sanitizeHTML(cls.endTime)}</strong>
                            <span class="badge" style="background: ${color}; color: white;">${Utils.sanitizeHTML(formation?.name || 'Formación')}</span>
                        </div>
                        <p style="margin: 0.25rem 0; font-weight: 600;">${Utils.sanitizeHTML(subject?.name || 'Asignatura')}</p>
                        <p style="margin: 0; font-size: 0.875rem; color: var(--text-secondary);">
                            <i class="fas fa-map-marker-alt"></i> ${Utils.sanitizeHTML(cls.classroom || 'Sin aula')}
                        </p>
                    </div>
                `;
            }).join('');
        }

        Utils.openModal('Actividad del Profesor', content);
    },

    // Colors go into style attributes: only a hex value is accepted (see programming-writes.js).
    safeColor(value) {
        return /^#[0-9a-f]{3,8}$/i.test(value || '') ? value : null;
    },

    getColorForClass(cls) {
        const subject = DataManager.getSubject(cls.subjectId);
        return this.safeColor(subject?.color) || this.getColorForFormation(cls.formationId);
    },

    getColorForFormation(formationId) {
        // Fallback hash-based color if no subject color is found
        if (!formationId) return '#6c757d';
        const colors = [
            '#667eea', '#764ba2', '#f093fb', '#4facfe',
            '#43e97b', '#fa709a', '#30cfd0', '#a8edea'
        ];
        const hash = formationId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
        return colors[hash % colors.length];
    },

    downloadPDF(professorId) {
        const professor = DataManager.getProfessor(professorId);
        if (!professor) return;
        if (!Utils.whenPdfReady(() => this.downloadPDF(professorId))) return;

        const classes = DataManager.getClassesByProfessor(professorId);
        const stats = ProfessorService.getProfessorStats(professorId);

        // Create PDF using jsPDF
        const { jsPDF } = window.jspdf;
        const doc = new jsPDF();

        // Header
        doc.setFontSize(20);
        doc.setTextColor(40, 40, 40);
        doc.text('Información del Profesor', 105, 20, { align: 'center' });

        // Professor Name
        doc.setFontSize(16);
        doc.setTextColor(60, 60, 60);
        doc.text(`${professor.firstName} ${professor.lastName}`, 105, 35, { align: 'center' });

        // Separator line
        doc.setDrawColor(200, 200, 200);
        doc.line(20, 42, 190, 42);

        let yPos = 55;

        // Personal Information Section
        doc.setFontSize(14);
        doc.setTextColor(40, 40, 40);
        doc.text('Datos Personales', 20, yPos);
        yPos += 10;

        doc.setFontSize(10);
        doc.setTextColor(80, 80, 80);

        const personalData = [
            { label: 'DNI:', value: professor.dni || 'No especificado' },
            { label: 'Nº SS:', value: professor.ssNumber || 'No especificado' },
            { label: 'Email:', value: professor.email || 'No especificado' },
            { label: 'Teléfono:', value: professor.phone || 'No especificado' },
            { label: 'Dirección:', value: professor.address || 'No especificado' }
        ];

        personalData.forEach(item => {
            doc.setFont(undefined, 'bold');
            doc.text(item.label, 20, yPos);
            doc.setFont(undefined, 'normal');
            doc.text(item.value, 60, yPos);
            yPos += 7;
        });

        yPos += 5;

        // Economic Information Section
        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(40, 40, 40);
        doc.text('Datos Económicos', 20, yPos);
        yPos += 10;

        doc.setFontSize(10);
        doc.setTextColor(80, 80, 80);

        const economicData = [
            { label: 'Nº de Cuenta:', value: professor.bankAccount || 'No especificado' },
            { label: 'Especialidad:', value: professor.specialty || 'No especificado' },
            StaffPay.isFixed(professor) ? { label: 'Sueldo neto/mes:', value: Utils.formatCurrency(StaffPay.monthlySalary(professor)) }
                : { label: 'Tarifa/hora bruta:', value: Utils.formatCurrency(professor.hourlyRate) }
        ];

        economicData.forEach(item => {
            doc.setFont(undefined, 'bold');
            doc.text(item.label, 20, yPos);
            doc.setFont(undefined, 'normal');
            doc.text(item.value, 60, yPos);
            yPos += 7;
        });

        yPos += 5;

        // Statistics Section
        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(40, 40, 40);
        doc.text('Estadísticas', 20, yPos);
        yPos += 10;

        doc.setFontSize(10);
        doc.setTextColor(80, 80, 80);

        const statsData = [
            { label: 'Horas Semanales:', value: `${stats.weeklyHours.toFixed(2)} h` },
            { label: 'Coste Semanal:', value: Utils.formatCurrency(stats.weeklyCost) },
            { label: 'Total Asignaturas:', value: stats.totalSubjects },
            { label: 'Total Clases:', value: stats.totalClasses }
        ];

        statsData.forEach(item => {
            doc.setFont(undefined, 'bold');
            doc.text(item.label, 20, yPos);
            doc.setFont(undefined, 'normal');
            doc.text(String(item.value), 60, yPos);
            yPos += 7;
        });

        // Footer
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(`Generado el ${new Date().toLocaleDateString('es-ES')} a las ${new Date().toLocaleTimeString('es-ES')}`, 105, 280, { align: 'center' });

        // Save PDF
        doc.save(`profesor-${professor.firstName}-${professor.lastName}-${new Date().toISOString().split('T')[0]}.pdf`);

        Utils.showToast('PDF descargado correctamente', 'success');
    },

    refreshCalendar(professorId) {
        App.navigate('profesor', professorId);
    },

    async confirmDelete(professorId) {
        const confirmation = await SafeHTML.fire({
            title: 'Eliminar profesor definitivamente',
            text: 'Se eliminarán su ficha, cuentas, fotos, nóminas y documentos vinculados. Sus clases y asignaturas se conservarán sin profesor asignado. Escribe ELIMINAR para confirmar.',
            input: 'text', showCancelButton: true, confirmButtonText: 'Eliminar definitivamente', cancelButtonText: 'Cancelar', focusCancel: true,
            inputValidator: value => String(value || '').trim().toUpperCase() === 'ELIMINAR' ? undefined : 'Escribe ELIMINAR para confirmar.'
        });
        if (!confirmation.isConfirmed || this._deletingProfessor) return;
        this._deletingProfessor = true;
        try {
            await DataManager.deleteProfessor(professorId);
            AuthManager.clearCachedData();
            window.location.assign('/profesores');
        } catch (error) { Utils.showToast(error.message, 'error'); }
        finally { this._deletingProfessor = false; }
    },

    showContactInfoModal(professorId) {
        const professor = DataManager.getProfessor(professorId);
        if (!professor) return;

        const displayUsername = this.accessUsername(professor);

        const fields = [
            { label: 'Nombre Completo', value: `${professor.firstName} ${professor.lastName}`, icon: 'fa-user' },
            { label: 'Usuario de Acceso', value: displayUsername, icon: 'fa-user-cog' },
            { label: 'DNI', value: professor.dni, icon: 'fa-id-card' },
            { label: 'Nº SS', value: professor.ssNumber, icon: 'fa-shield-alt' },
            { label: 'Email', value: professor.email, icon: 'fa-envelope' },
            { label: 'Teléfono', value: professor.phone, icon: 'fa-phone' },
            { label: 'Dirección', value: professor.address, icon: 'fa-map-marker-alt' },
            { label: 'Nº de Cuenta', value: professor.bankAccount, icon: 'fa-university' },
            { label: 'Especialidad', value: professor.specialty, icon: 'fa-briefcase' },
            StaffPay.isFixed(professor) ? { label: 'Sueldo neto mensual (12 pagas)', value: Utils.formatCurrency(StaffPay.monthlySalary(professor)), icon: 'fa-euro-sign' }
                : { label: 'Tarifa por Hora (bruta)', value: Utils.formatCurrency(professor.hourlyRate), icon: 'fa-euro-sign' }
        ];

        const content = `
            <div style="display: flex; flex-direction: column; gap: 1rem;">
                ${fields.map(field => {
            const displayValue = field.value || 'No especificado';
            return `
                        <div style="display: flex; align-items: center; justify-content: space-between; padding: 0.75rem; background: var(--background-secondary); border-radius: 8px; border: 1px solid var(--border-color);">
                            <div style="display: flex; align-items: center; gap: 0.75rem; flex: 1;">
                                <i class="fas ${field.icon}" style="color: var(--primary-color); width: 20px; text-align: center;"></i>
                                <div style="flex: 1;">
                                    <div style="font-size: 0.75rem; color: var(--text-secondary); margin-bottom: 0.25rem;">${Utils.sanitizeHTML(field.label)}</div>
                                    <div style="font-weight: 500; color: ${field.value ? 'var(--text-primary)' : 'var(--text-secondary)'};">${Utils.sanitizeHTML(displayValue)}</div>
                                </div>
                            </div>
                            ${field.value ? `
                                <button class="btn btn-secondary btn-sm" ${UIEvents.attr("click", function(event,captured){ProfessorProfile.copyToClipboard(String(captured[0]),String(captured[1]));}, [field.value, field.label])} style="padding: 0.5rem 0.75rem;">
                                    <i class="fas fa-copy"></i>
                                </button>
                            ` : ''}
                        </div>
                    `;
        }).join('')}
            </div>
        `;

        Utils.openModal('Información de Contacto', content);
    },

    copyToClipboard(text, fieldName) {
        // Create a temporary textarea element
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);

        // Select and copy
        textarea.select();
        document.execCommand('copy');

        // Remove the textarea
        document.body.removeChild(textarea);

        // Show success message
        Utils.showToast(`${fieldName} copiado al portapapeles`, 'success');
    },


    // Photo chosen in the ficha form: framed now, uploaded by saveProfessor once the ficha exists.
    async pickFormPhoto(input) {
        const picked = input.files[0];
        input.value = '';
        if (!picked) return;
        if (!['image/png', 'image/jpeg', 'image/gif', 'image/webp'].includes(picked.type) || picked.size > 10 * 1024 * 1024) {
            Utils.showToast('Selecciona una imagen PNG, JPG, GIF o WebP de hasta 10 MB.', 'error');
            return;
        }
        const file = await PhotoCropper.open(picked);
        const button = document.getElementById('photo-preview');
        if (!file || !button) return;
        this._pendingPhoto = file;
        if (this._pendingPhotoUrl) URL.revokeObjectURL(this._pendingPhotoUrl);
        this._pendingPhotoUrl = URL.createObjectURL(file);
        const img = document.createElement('img');
        img.src = this._pendingPhotoUrl;
        img.alt = '';
        button.querySelector('img, i:not(.fa-camera)')?.remove();
        button.prepend(img);
    },

    /** Uploads a (framed) photo for the ficha and caches it. Throws with the message to show. */
    async uploadPhotoFile(professorId, file) {
        const formData = new FormData();
        formData.append('photo', file);
        const token = typeof AuthManager !== 'undefined' ? AuthManager.getCsrfToken() : '';
        const response = await fetch(`/api/professors/${encodeURIComponent(professorId)}/photo`, {
            method: 'POST', headers: token ? { 'X-SICAF-CSRF': token } : {}, body: formData
        });
        const data = await response.json().catch(() => ({}));
        if (!data.success || !data.photoUrl) throw new Error(data.error || 'Error al subir la imagen');
        ProfessorService.cacheProfessor({ id: professorId, photo: data.photoUrl });
        return data.photoUrl;
    },

    async uploadAvatar(professorId, event) {
        const picked = event.target.files[0];
        event.target.value = ''; // The same file can be chosen again after cancelling.
        if (!picked) return;

        if (!['image/png', 'image/jpeg', 'image/gif', 'image/webp'].includes(picked.type)) {
            Utils.showToast('Selecciona una imagen PNG, JPG, GIF o WebP.', 'error');
            return;
        }

        if (picked.size > 10 * 1024 * 1024) {
            Utils.showToast('La imagen es demasiado grande. Tamaño máximo: 10MB', 'error');
            return;
        }
        const file = await PhotoCropper.open(picked);
        if (!file) return;

        Utils.showToast('Subiendo foto de perfil...', 'info');

        try {
            await this.uploadPhotoFile(professorId, file);
            Utils.showToast('Foto de perfil actualizada correctamente', 'success');
            App.navigate(App.currentRoute(), false);
        } catch (error) {
            console.error('Error al subir avatar:', error);
            Utils.showToast(error.message || 'Error de conexión al subir la foto', 'error');
        }
    },

    changeAcademicYear(professorId, yearStart) {
        this.selectedYearStart = parseInt(yearStart, 10);
        const hash = App.currentRoute();
        App.navigate(hash);
    }
};

window.ProfessorProfile = ProfessorProfile;
